/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import './graphene.css';

import {AnnotationDisplayState, AnnotationLayerState} from 'neuroglancer/annotation/annotation_layer_state';
import {LayerChunkProgressInfo} from 'neuroglancer/chunk_manager/base';
import {Annotation, AnnotationReference, AnnotationSource, AnnotationType, Line, LocalAnnotationSource, makeDataBoundsBoundingBoxAnnotationSet, Point} from 'neuroglancer/annotation';
import {ChunkManager, WithParameters} from 'neuroglancer/chunk_manager/frontend';
import {makeIdentityTransform} from 'neuroglancer/coordinate_transform';
import {CredentialsManager} from 'neuroglancer/credentials_provider';
import {WithCredentialsProvider} from 'neuroglancer/credentials_provider/chunk_source_frontend';
import {DataSource, DataSubsourceEntry, GetDataSourceOptions, RedirectError} from 'neuroglancer/datasource';
import {CHUNKED_GRAPH_LAYER_RPC_ID, CHUNKED_GRAPH_RENDER_LAYER_UPDATE_SOURCES_RPC_ID, ChunkedGraphChunkSource as ChunkedGraphChunkSourceInterface, ChunkedGraphChunkSpecification, ChunkedGraphSourceParameters, getGrapheneFragmentKey, GRAPHENE_MESH_NEW_SEGMENT_RPC_ID, isBaseSegmentId, makeChunkedGraphChunkSpecification, MeshSourceParameters, MultiscaleMeshMetadata, PYCG_APP_VERSION, responseIdentity} from 'neuroglancer/datasource/graphene/base';
import {DataEncoding, ShardingHashFunction, ShardingParameters} from 'neuroglancer/datasource/precomputed/base';
import {getSegmentPropertyMap, MultiscaleVolumeInfo, parseMultiscaleVolumeInfo, parseProviderUrl, PrecomputedDataSource, PrecomputedMultiscaleVolumeChunkSource, resolvePath} from 'neuroglancer/datasource/precomputed/frontend';
import {LayerView, MouseSelectionState, VisibleLayerInfo} from 'neuroglancer/layer';
import {LoadedDataSubsource} from 'neuroglancer/layer_data_source';
import {MeshLayer, MeshSource, MultiscaleMeshLayer} from 'neuroglancer/mesh/frontend';
import {DisplayDimensionRenderInfo} from 'neuroglancer/navigation_state';
import {ChunkTransformParameters, getChunkPositionFromCombinedGlobalLocalPositions, getChunkTransformParameters, RenderLayerTransformOrError} from 'neuroglancer/render_coordinate_transform';
import {RenderLayer, RenderLayerRole} from 'neuroglancer/renderlayer';
import {augmentSegmentId, resetTemporaryVisibleSegmentsState, SegmentationDisplayState3D, SegmentationLayerSharedObject, SegmentWidgetFactory, Uint64MapEntry} from 'neuroglancer/segmentation_display_state/frontend';
import {VisibleSegmentEquivalencePolicy} from 'neuroglancer/segmentation_graph/segment_id';
import {ComputedSplit, SegmentationGraphSource, SegmentationGraphSourceConnection, SegmentationGraphSourceTab} from 'neuroglancer/segmentation_graph/source';
import {SegmentationUserLayer} from 'neuroglancer/segmentation_user_layer';
import {SharedWatchableValue} from 'neuroglancer/shared_watchable_value';
import {FrontendTransformedSource, getVolumetricTransformedSources, serializeAllTransformedSources, SliceViewChunkSource, SliceViewSingleResolutionSource} from 'neuroglancer/sliceview/frontend';
import {SliceViewPanelRenderLayer, SliceViewRenderLayer} from 'neuroglancer/sliceview/renderlayer';
import {StatusMessage} from 'neuroglancer/status';
import {TrackableBoolean, TrackableBooleanCheckbox} from 'neuroglancer/trackable_boolean';
import {makeCachedLazyDerivedWatchableValue, NestedStateManager, registerNested, TrackableValue, WatchableSet, WatchableValue, WatchableValueInterface} from 'neuroglancer/trackable_value';
import {AnnotationLayerView, MergedAnnotationStates, PlaceLineTool, makeAnnotationListElement} from 'neuroglancer/ui/annotations';
import {LayerTool, makeToolActivationStatusMessageWithHeader, makeToolButton, registerLegacyTool, registerTool, ToolActivation} from 'neuroglancer/ui/tool';
import {Uint64Set} from 'neuroglancer/uint64_set';
import {packColor} from 'neuroglancer/util/color';
import {Owned, RefCounted} from 'neuroglancer/util/disposable';
import {makeValueOrError, ValueOrError, valueOrThrow} from 'neuroglancer/util/error';
import {EventActionMap} from 'neuroglancer/util/event_action_map';
import {mat4, vec3, vec4} from 'neuroglancer/util/geom';
import {HttpError, isNotFoundError, responseArrayBuffer, responseJson} from 'neuroglancer/util/http_request';
import {VolumeChunkEncoding} from 'neuroglancer/datasource/precomputed/base';
import {readSingleChannelValue as ngeReadCompressedUint64} from 'neuroglancer/sliceview/compressed_segmentation/decode_uint64';
import {parseArray, parseFixedLengthArray, verify3dVec, verifyBoolean, verifyEnumString, verifyFiniteFloat, verifyFinitePositiveFloat, verifyInt, verifyNonnegativeInt, verifyObject, verifyObjectProperty, verifyOptionalObjectProperty, verifyOptionalString, verifyPositiveInt, verifyString} from 'neuroglancer/util/json';
import {getObjectId} from 'neuroglancer/util/object_id';
import {NullarySignal} from 'neuroglancer/util/signal';
import {cancellableFetchSpecialOk, parseSpecialUrl, SpecialProtocolCredentials, SpecialProtocolCredentialsProvider} from 'neuroglancer/util/special_protocol_request';
import {CancellationToken, CancellationTokenSource, uncancelableToken} from 'neuroglancer/util/cancellation';
import {Trackable} from 'neuroglancer/util/trackable';
import {Uint64} from 'neuroglancer/util/uint64';
import {makeDeleteButton} from 'neuroglancer/widget/delete_button';
import {DependentViewContext} from 'neuroglancer/widget/dependent_view_widget';
import {makeIcon} from 'neuroglancer/widget/icon';
import {MultiscaleAnnotationSource} from 'neuroglancer/annotation/frontend_source';
import {removeChildren} from 'neuroglancer/util/dom';
import {getDefaultAnnotationListBindings} from 'neuroglancer/ui/default_input_event_bindings';
import {MouseEventBinder} from 'neuroglancer/util/mouse_bindings';

function vec4FromVec3(vec: vec3, alpha = 0) {
  const res = vec4.clone([...vec]);
  res[3] = alpha;
  return res;
}

const RED_COLOR = vec3.fromValues(1, 0, 0);
const BLUE_COLOR = vec3.fromValues(0, 0, 1);
const RED_COLOR_SEGMENT = vec4FromVec3(RED_COLOR, 0.5);
const BLUE_COLOR_SEGMENT = vec4FromVec3(BLUE_COLOR, 0.5);
const RED_COLOR_HIGHLIGHT = vec4FromVec3(RED_COLOR, 0.25);
const BLUE_COLOR_HIGHTLIGHT = vec4FromVec3(BLUE_COLOR, 0.25);
const TRANSPARENT_COLOR = vec4.fromValues(0.5, 0.5, 0.5, 0.01);
const RED_COLOR_SEGMENT_PACKED = new Uint64(packColor(RED_COLOR_SEGMENT));
const BLUE_COLOR_SEGMENT_PACKED = new Uint64(packColor(BLUE_COLOR_SEGMENT));
const TRANSPARENT_COLOR_PACKED = new Uint64(packColor(TRANSPARENT_COLOR));
const MULTICUT_OFF_COLOR = vec4.fromValues(0, 0, 0, 0.5);
const WHITE_COLOR = vec3.fromValues(1, 1, 1);

class GrapheneMeshSource extends
(WithParameters(WithCredentialsProvider<SpecialProtocolCredentials>()(MeshSource), MeshSourceParameters)) {
  getFragmentKey(objectKey: string|null, fragmentId: string) {
    objectKey;
    return getGrapheneFragmentKey(fragmentId);
  }
}

class AppInfo {
  segmentationUrl: string;
  meshingUrl: string;
  supported_api_versions: number[];
  constructor(infoUrl: string, obj: any) {
    // .../1.0/... is the legacy link style
    // .../table/... is the current, version agnostic link style (for retrieving the info file)
    const linkStyle = /^(https?:\/\/[.\w:\-\/]+)\/segmentation\/(?:1\.0|table)\/([^\/]+)\/?$/;
    let match = infoUrl.match(linkStyle);
    if (match === null) {
      throw Error(`Graph URL invalid: ${infoUrl}`);
    }
    this.segmentationUrl = `${match[1]}/segmentation/api/v${PYCG_APP_VERSION}/table/${match[2]}`;
    this.meshingUrl = `${match[1]}/meshing/api/v${PYCG_APP_VERSION}/table/${match[2]}`;

    try {
      verifyObject(obj);
      this.supported_api_versions = verifyObjectProperty(
          obj, 'supported_api_versions', x => parseArray(x, verifyNonnegativeInt));
    } catch (error) {
      // Dealing with a prehistoric graph server with no version information
      this.supported_api_versions = [0];
    }
    if (PYCG_APP_VERSION in this.supported_api_versions === false) {
      const redirectMsg = `This Neuroglancer branch requires Graph Server version ${
          PYCG_APP_VERSION}, but the server only supports version(s) ${
          this.supported_api_versions}.`;
      throw new Error(redirectMsg);
    }
  }
}

const N_BITS_FOR_LAYER_ID_DEFAULT = 8;

class GraphInfo {
  chunkSize: vec3;
  nBitsForLayerId: number;
  constructor(obj: any) {
    verifyObject(obj);
    this.chunkSize = verifyObjectProperty(
        obj, 'chunk_size', x => parseFixedLengthArray(vec3.create(), x, verifyPositiveInt));
    this.nBitsForLayerId = verifyOptionalObjectProperty(
        obj, 'n_bits_for_layer_id', verifyPositiveInt, N_BITS_FOR_LAYER_ID_DEFAULT);
  }
}

interface GrapheneMultiscaleVolumeInfo extends MultiscaleVolumeInfo {
  dataUrl: string;
  app: AppInfo;
  graph: GraphInfo;
}

function parseGrapheneMultiscaleVolumeInfo(
    obj: unknown, url: string,
    credentialsManager: CredentialsManager): GrapheneMultiscaleVolumeInfo {
  const volumeInfo = parseMultiscaleVolumeInfo(obj);
  const dataUrl =
      verifyObjectProperty(obj, 'data_dir', x => parseSpecialUrl(x, credentialsManager)).url;
  const app = verifyObjectProperty(obj, 'app', x => new AppInfo(url, x));
  const graph = verifyObjectProperty(obj, 'graph', x => new GraphInfo(x));
  return {
    ...volumeInfo,
    app,
    graph,
    dataUrl,
  };
}

class GrapheneMultiscaleVolumeChunkSource extends PrecomputedMultiscaleVolumeChunkSource {
  constructor(
      chunkManager: ChunkManager,
      public chunkedGraphCredentialsProvider: SpecialProtocolCredentialsProvider,
      public info: GrapheneMultiscaleVolumeInfo) {
    super(chunkManager, undefined, info.dataUrl, info);
  }

  getChunkedGraphSource() {
    const {rank} = this;
    const scaleInfo = this.info.scales[0];

    const spec = makeChunkedGraphChunkSpecification({
      rank,
      dataType: this.info.dataType,
      upperVoxelBound: scaleInfo.size,
      chunkDataSize: Uint32Array.from(this.info.graph.chunkSize),
      baseVoxelOffset: scaleInfo.voxelOffset,
    });

    const stride = rank + 1;
    const chunkToMultiscaleTransform = new Float32Array(stride * stride);
    chunkToMultiscaleTransform[chunkToMultiscaleTransform.length - 1] = 1;
    const {lowerBounds: baseLowerBound, upperBounds: baseUpperBound} =
        this.info.modelSpace.boundingBoxes[0].box;
    const lowerClipBound = new Float32Array(rank);
    const upperClipBound = new Float32Array(rank);

    for (let i = 0; i < 3; ++i) {
      const relativeScale = 1;
      chunkToMultiscaleTransform[stride * i + i] = relativeScale;
      chunkToMultiscaleTransform[stride * rank + i] = scaleInfo.voxelOffset[i];
      lowerClipBound[i] = baseLowerBound[i];
      upperClipBound[i] = baseUpperBound[i];
    }
    return {
      chunkSource: this.chunkManager.getChunkSource(GrapheneChunkedGraphChunkSource, {
        spec,
        credentialsProvider: this.chunkedGraphCredentialsProvider,
        parameters: {url: `${this.info.app!.segmentationUrl}/node`}
      }),
      chunkToMultiscaleTransform,
      lowerClipBound,
      upperClipBound,
    };
  }
}

function parseTransform(data: any): mat4 {
  return verifyObjectProperty(data, 'transform', value => {
    const transform = mat4.create();
    if (value !== undefined) {
      parseFixedLengthArray(transform.subarray(0, 12), value, verifyFiniteFloat);
    }
    mat4.transpose(transform, transform);
    return transform;
  });
}

interface ParsedMeshMetadata {
  metadata: MultiscaleMeshMetadata|undefined;
  segmentPropertyMap?: string|undefined;
}

function parseMeshMetadata(data: any): ParsedMeshMetadata {
  verifyObject(data);
  const t = verifyObjectProperty(data, '@type', verifyString);
  let metadata: MultiscaleMeshMetadata|undefined;
  if (t === 'neuroglancer_legacy_mesh') {
    const sharding = verifyObjectProperty(data, 'sharding', parseGrapheneShardingParameters);
    if (sharding === undefined) {
      metadata = undefined;
    } else {
      const lodScaleMultiplier = 0;
      const vertexQuantizationBits = 10;
      const transform = parseTransform(data);
      metadata = {lodScaleMultiplier, transform, sharding, vertexQuantizationBits};
    }
  } else if (t !== 'neuroglancer_multilod_draco') {
    throw new Error(`Unsupported mesh type: ${JSON.stringify(t)}`);
  } else {
    const lodScaleMultiplier =
        verifyObjectProperty(data, 'lod_scale_multiplier', verifyFinitePositiveFloat);
    const vertexQuantizationBits =
        verifyObjectProperty(data, 'vertex_quantization_bits', verifyPositiveInt);
    const transform = parseTransform(data);
    const sharding = verifyObjectProperty(data, 'sharding', parseGrapheneShardingParameters);
    metadata = {lodScaleMultiplier, transform, sharding, vertexQuantizationBits};
  }
  const segmentPropertyMap = verifyObjectProperty(data, 'segment_properties', verifyOptionalString);
  return {metadata, segmentPropertyMap};
}

async function getMeshMetadata(
    chunkManager: ChunkManager, credentialsProvider: SpecialProtocolCredentialsProvider,
    url: string): Promise<ParsedMeshMetadata> {
  let metadata: any;
  try {
    metadata = await getJsonMetadata(chunkManager, credentialsProvider, url);
  } catch (e) {
    if (isNotFoundError(e)) {
      // If we fail to fetch the info file, assume it is the legacy
      // single-resolution mesh format.
      return {metadata: undefined};
    }
    throw e;
  }
  return parseMeshMetadata(metadata);
}

function parseShardingEncoding(y: any): DataEncoding {
  if (y === undefined) return DataEncoding.RAW;
  return verifyEnumString(y, DataEncoding);
}

function parseShardingParameters(shardingData: any): ShardingParameters|undefined {
  if (shardingData === undefined) return undefined;
  verifyObject(shardingData);
  const t = verifyObjectProperty(shardingData, '@type', verifyString);
  if (t !== 'neuroglancer_uint64_sharded_v1') {
    throw new Error(`Unsupported sharding format: ${JSON.stringify(t)}`);
  }
  const hash =
      verifyObjectProperty(shardingData, 'hash', y => verifyEnumString(y, ShardingHashFunction));
  const preshiftBits = verifyObjectProperty(shardingData, 'preshift_bits', verifyInt);
  const shardBits = verifyObjectProperty(shardingData, 'shard_bits', verifyInt);
  const minishardBits = verifyObjectProperty(shardingData, 'minishard_bits', verifyInt);
  const minishardIndexEncoding =
      verifyObjectProperty(shardingData, 'minishard_index_encoding', parseShardingEncoding);
  const dataEncoding = verifyObjectProperty(shardingData, 'data_encoding', parseShardingEncoding);
  return {hash, preshiftBits, shardBits, minishardBits, minishardIndexEncoding, dataEncoding};
}

function parseGrapheneShardingParameters(shardingData: any): Array<ShardingParameters>|undefined {
  if (shardingData === undefined) return undefined;
  verifyObject(shardingData);
  let grapheneShardingParameters = new Array<ShardingParameters>();
  for (const layer in shardingData) {
    let index = Number(layer);
    grapheneShardingParameters[index] = parseShardingParameters(shardingData[index])!;
  }
  return grapheneShardingParameters;
}

function getShardedMeshSource(
    chunkManager: ChunkManager, parameters: MeshSourceParameters,
    credentialsProvider: SpecialProtocolCredentialsProvider) {
  return chunkManager.getChunkSource(GrapheneMeshSource, {parameters, credentialsProvider});
}

async function getMeshSource(
    chunkManager: ChunkManager, credentialsProvider: SpecialProtocolCredentialsProvider,
    url: string, fragmentUrl: string, nBitsForLayerId: number) {
  const {metadata, segmentPropertyMap} =
      await getMeshMetadata(chunkManager, undefined, fragmentUrl);
  const parameters: MeshSourceParameters = {
    manifestUrl: url,
    fragmentUrl: fragmentUrl,
    lod: 0,
    sharding: metadata?.sharding,
    nBitsForLayerId,
  };
  const transform = metadata?.transform || mat4.create();
  return {
    source: getShardedMeshSource(chunkManager, parameters, credentialsProvider),
    transform,
    segmentPropertyMap,
  };
}

function getJsonMetadata(
    chunkManager: ChunkManager, credentialsProvider: SpecialProtocolCredentialsProvider,
    url: string): Promise<any> {
  return chunkManager.memoize.getUncounted(
      {'type': 'graphene:metadata', url, credentialsProvider: getObjectId(credentialsProvider)},
      async () => {
        return await cancellableFetchSpecialOk(
            credentialsProvider, `${url}/info`, {}, responseJson);
      });
}

function getSubsourceToModelSubspaceTransform(info: MultiscaleVolumeInfo) {
  const m = mat4.create();
  const resolution = info.scales[0].resolution;
  for (let i = 0; i < 3; ++i) {
    m[5 * i] = 1 / resolution[i];
  }
  return m;
}

async function getVolumeDataSource(
    options: GetDataSourceOptions, credentialsProvider: SpecialProtocolCredentialsProvider,
    url: string, metadata: any): Promise<DataSource> {
  const info = parseGrapheneMultiscaleVolumeInfo(metadata, url, options.credentialsManager);
  const volume =
      new GrapheneMultiscaleVolumeChunkSource(options.chunkManager, credentialsProvider, info);
  const state = new GrapheneState();
  if (options.state) {
    state.restoreState(options.state)
  }
  const segmentationGraph = new GrapheneGraphSource(info, credentialsProvider, volume, state);
  const {modelSpace} = info;
  const subsources: DataSubsourceEntry[] = [
    {
      id: 'default',
      default: true,
      subsource: {volume},
    },
    {
      id: 'graph',
      default: true,
      subsource: {segmentationGraph},
    },
    {
      id: 'bounds',
      default: true,
      subsource: {
        staticAnnotations: makeDataBoundsBoundingBoxAnnotationSet(modelSpace.bounds),
      },
    },
  ];
  if (info.segmentPropertyMap !== undefined) {
    const mapUrl = resolvePath(url, info.segmentPropertyMap);
    const metadata = await getJsonMetadata(options.chunkManager, credentialsProvider, mapUrl);
    const segmentPropertyMap =
        getSegmentPropertyMap(options.chunkManager, credentialsProvider, metadata, mapUrl);
    subsources.push({
      id: 'properties',
      default: true,
      subsource: {segmentPropertyMap},
    });
  }
  if (info.mesh !== undefined) {
    const {source: meshSource, transform} = await getMeshSource(
        options.chunkManager, credentialsProvider, info.app!.meshingUrl,
        resolvePath(info.dataUrl, info.mesh), info.graph.nBitsForLayerId);
    const subsourceToModelSubspaceTransform = getSubsourceToModelSubspaceTransform(info);
    mat4.multiply(subsourceToModelSubspaceTransform, subsourceToModelSubspaceTransform, transform);
    subsources.push({
      id: 'mesh',
      default: true,
      subsource: {mesh: meshSource},
      subsourceToModelSubspaceTransform,
    });
  }
  return {modelTransform: makeIdentityTransform(modelSpace), subsources, state};
}

export class GrapheneDataSource extends PrecomputedDataSource {
  get description() {
    return 'Graphene file-backed data source';
  }

  get(options: GetDataSourceOptions): Promise<DataSource> {
    const {url: providerUrl, parameters} = parseProviderUrl(options.providerUrl);
    return options.chunkManager.memoize.getUncounted(
        {'type': 'graphene:get', providerUrl, parameters}, async(): Promise<DataSource> => {
          const {url, credentialsProvider} =
              parseSpecialUrl(providerUrl, options.credentialsManager);
          let metadata: any;
          try {
            metadata = await getJsonMetadata(options.chunkManager, credentialsProvider, url);
          } catch (e) {
            if (isNotFoundError(e)) {
              if (parameters['type'] === 'mesh') {
                console.log('does this happen?');
              }
            }
            throw e;
          }
          verifyObject(metadata);
          const redirect = verifyOptionalObjectProperty(metadata, 'redirect', verifyString);
          if (redirect !== undefined) {
            throw new RedirectError(redirect);
          }
          const t = verifyOptionalObjectProperty(metadata, '@type', verifyString);
          switch (t) {
            case 'neuroglancer_multiscale_volume':
            case undefined:
              return await getVolumeDataSource(options, credentialsProvider, url, metadata);
            default:
              throw new Error(`Invalid type: ${JSON.stringify(t)}`);
          }
        });
  }
}

function getGraphLoadedSubsource(layer: SegmentationUserLayer) {
  for (const dataSource of layer.dataSources) {
    const {loadState} = dataSource;
    if (loadState === undefined || loadState.error !== undefined) continue;
    for (const subsource of loadState.subsources) {
      if (subsource.enabled) {
        if (subsource.subsourceEntry.id === 'graph') {
          return subsource;
        }
      }
    }
  }
  return undefined;
}

function makeColoredAnnotationState(
    layer: SegmentationUserLayer, loadedSubsource: LoadedDataSubsource,
    subsubsourceId: string, color: vec3, readonly=false) {
  const {subsourceEntry} = loadedSubsource;
  const source = new LocalAnnotationSource(loadedSubsource.loadedDataSource.transform, [], ['associated segments']);
  source.readonly = readonly;
  
  const displayState = new AnnotationDisplayState();
  displayState.color.value.set(color);

  displayState.relationshipStates.set('associated segments', {
    segmentationState: new WatchableValue(layer.displayState),
    showMatches: new TrackableBoolean(false),
  });

  const state = new AnnotationLayerState({
    localPosition: layer.localPosition,
    transform: loadedSubsource.getRenderLayerTransform(),
    source,
    displayState,
    dataSource: loadedSubsource.loadedDataSource.layerDataSource,
    subsourceIndex: loadedSubsource.subsourceIndex,
    subsourceId: subsourceEntry.id,
    subsubsourceId,
    role: RenderLayerRole.ANNOTATION,
  });
  layer.addAnnotationLayerState(state, loadedSubsource);
  return state;
}

function getOptionalUint64(obj: any, key: string) {
  return verifyOptionalObjectProperty(obj, key, value => Uint64.parseString(String(value)));
}

function getUint64(obj: any, key: string) {
  return verifyObjectProperty(obj, key, value => Uint64.parseString(String(value)));
}

function restoreSegmentSelection(obj: any): SegmentSelection {
  const segmentId = getUint64(obj, SEGMENT_ID_JSON_KEY);
  const rootId = getUint64(obj, ROOT_ID_JSON_KEY);
  const position = verifyObjectProperty(
    obj, POSITION_JSON_KEY, value => {
      return verify3dVec(value);
    });
  return {
    segmentId,
    rootId,
    position,
  }
}

const segmentSelectionToJSON = (x: SegmentSelection) => {
  return {
    [SEGMENT_ID_JSON_KEY]: x.segmentId.toJSON(),
    [ROOT_ID_JSON_KEY]: x.rootId.toJSON(),
    [POSITION_JSON_KEY]: [...x.position],
  }
}

const ID_JSON_KEY = 'id';
const SEGMENT_ID_JSON_KEY = 'segmentId';
const ROOT_ID_JSON_KEY = 'rootId';
const POSITION_JSON_KEY = 'position';
const SINK_JSON_KEY = 'sink';
const SOURCE_JSON_KEY = 'source';

const MULTICUT_JSON_KEY = 'multicut';
const FOCUS_SEGMENT_JSON_KEY = 'focusSegment';
const SINKS_JSON_KEY = 'sinks';
const SOURCES_JSON_KEY = 'sources';

const MERGE_JSON_KEY = 'merge';
const MERGES_JSON_KEY = 'merges';
const AUTOSUBMIT_JSON_KEY = 'autosubmit';
const LOCKED_JSON_KEY = 'locked';
const MERGED_ROOT_JSON_KEY = 'mergedRoot';
const ERROR_JSON_KEY = 'error';

const FIND_PATH_JSON_KEY = 'findPath';
const TARGET_JSON_KEY = "target";
const CENTROIDS_JSON_KEY = "centroids";
const PRECISION_MODE_JSON_KEY = "precision";




class GrapheneState extends RefCounted implements Trackable {
  changed = new NullarySignal();

  public multicutState = new MulticutState();
  public mergeState = new MergeState();
  public findPathState = new FindPathState();

  constructor() {
    super();
    this.registerDisposer(this.multicutState.changed.add(() => {
      this.changed.dispatch();
    }));
    this.registerDisposer(this.mergeState.changed.add(() => {
      this.changed.dispatch();
    }));
    this.registerDisposer(this.findPathState.changed.add(() => {
      this.changed.dispatch()
    }));
  }

  replaceSegments(oldValues: Uint64Set, newValues: Uint64Set) {
    console.log('replaceSegments', [...oldValues].map(x => x.toJSON()), [...newValues].map(x => x.toJSON()));
    this.multicutState.replaceSegments(oldValues, newValues);
    this.mergeState.replaceSegments(oldValues, newValues);
    this.findPathState.replaceSegments(oldValues, newValues);
  }

  reset() {
    this.multicutState.reset();
    this.mergeState.reset();
    this.findPathState.reset();
  }

  toJSON() {
    return {
      [MULTICUT_JSON_KEY]: this.multicutState.toJSON(),
      [MERGE_JSON_KEY]: this.mergeState.toJSON(),
      [FIND_PATH_JSON_KEY]: this.findPathState.toJSON(),
    }
  }

  restoreState(x: any) {
    verifyOptionalObjectProperty(x, MULTICUT_JSON_KEY, value => {
      this.multicutState.restoreState(value);
    });
    verifyOptionalObjectProperty(x, MERGE_JSON_KEY, value => {
      this.mergeState.restoreState(value);
    });
    verifyOptionalObjectProperty(x, FIND_PATH_JSON_KEY, value => {
      this.findPathState.restoreState(value);
    });
  }
}

export interface SegmentSelection {
  segmentId: Uint64;
  rootId: Uint64;
  position: Float32Array;
  annotationReference?: AnnotationReference;
}

class MergeState extends RefCounted implements Trackable {
  changed = new NullarySignal();

  merges = new WatchableValue<MergeSubmission[]>([]);
  autoSubmit = new TrackableBoolean(false);

  constructor() {
    super();
    this.registerDisposer(this.merges.changed.add(this.changed.dispatch));
  }

  replaceSegments(oldValues: Uint64Set, newValues: Uint64Set) {
    const {merges: {value: merges}} = this;
    const newValue = newValues.size === 1 ? [...newValues][0] : undefined;
    for (const merge of merges) {
      if (merge.source && oldValues.has(merge.source.rootId)) {
        if (newValue) {
          merge.source.rootId = newValue;
        } else {
          this.reset();
          return;
        }
      }
      if (merge.sink && oldValues.has(merge.sink.rootId)) {
        if (newValue) {
          merge.sink.rootId = newValue;
        } else {
          this.reset();
          return;
        }
      }
    }
  }

  reset() {
    this.merges.value = [];
    this.autoSubmit.reset();
  }

  toJSON() {
    const {merges, autoSubmit} = this;

    const mergeToJSON = (x: MergeSubmission) => {
      const res: any = {
        [ID_JSON_KEY]: x.id,
        [LOCKED_JSON_KEY]: x.locked,
        [SINK_JSON_KEY]: segmentSelectionToJSON(x.sink),
        [SOURCE_JSON_KEY]: segmentSelectionToJSON(x.source!),
      }

      if (x.mergedRoot) {
        res[MERGED_ROOT_JSON_KEY] = x.mergedRoot.toJSON();
      }
      if (x.error) {
        res[ERROR_JSON_KEY] = x.error;
      }

      return res;
    }

    return {
      [MERGES_JSON_KEY]: merges.value.filter(x=>x.source).map(mergeToJSON),
      [AUTOSUBMIT_JSON_KEY]: autoSubmit.toJSON(),
    }
  }

  restoreState(x: any) {
    function restoreSubmission(obj: any): MergeSubmission {
      const mergedRoot = getOptionalUint64(obj, MERGED_ROOT_JSON_KEY);
      const id = verifyObjectProperty(obj, ID_JSON_KEY, verifyString);
      const error = verifyOptionalObjectProperty(obj, ERROR_JSON_KEY, verifyString);
      const locked = verifyObjectProperty(obj, LOCKED_JSON_KEY, verifyBoolean);
      const sink = restoreSegmentSelection(obj[SINK_JSON_KEY]);
      const source = restoreSegmentSelection(obj[SOURCE_JSON_KEY]);
      return {
        id,
        locked,
        sink,
        source,
        mergedRoot,
        error,
      }
    }

    const submissionsValidator = (value: any) => {
      return parseArray(value, x => {
        return restoreSubmission(x);
      });
    };

    this.merges.value = verifyObjectProperty(x, MERGES_JSON_KEY, submissionsValidator);
    this.autoSubmit.restoreState(verifyOptionalObjectProperty(x, AUTOSUBMIT_JSON_KEY, verifyBoolean));
  }
}


class FindPathState extends RefCounted implements Trackable {
  changed = new NullarySignal();
  triggerPathUpdate = new NullarySignal();

  source = new TrackableValue<SegmentSelection|undefined>(undefined, x => x);
  target = new TrackableValue<SegmentSelection|undefined>(undefined, x => x);
  centroids = new TrackableValue<number[][]>([], x => x);
  precisionMode = new TrackableBoolean(true);

  get path() {
    const path: Line[] = [];
    const {
      source: {value: source},
      target: {value: target},
      centroids: {value: centroids}
    } = this;
    if (!source || !target || centroids.length === 0) {
      return path;
    }
    for (let i = 0; i < centroids.length - 1; i++) {
      const pointA = centroids[i];
      const pointB = centroids[i+1];
      const line: Line = {
        pointA: vec3.fromValues(pointA[0], pointA[1], pointA[2]),
        pointB: vec3.fromValues(pointB[0], pointB[1], pointB[2]),
        id: '',
        type: AnnotationType.LINE,
        properties: [],
      };
      path.push(line);
    }
    const firstLine: Line = {
      pointA: source.position,
      pointB: path[0].pointA,
      id: '',
      type: AnnotationType.LINE,
      properties: [],
    };
    const lastLine: Line = {
      pointA: path[path.length - 1].pointB,
      pointB: target.position,
      id: '',
      type: AnnotationType.LINE,
      properties: [],
    };

    return [firstLine, ...path, lastLine];
  }

  constructor() {
    super();
    this.registerDisposer(this.source.changed.add(() => {
      this.centroids.reset();
      this.changed.dispatch();
    }));
    this.registerDisposer(this.target.changed.add(() => {
      this.centroids.reset();
      this.changed.dispatch();
    }));
    this.registerDisposer(this.centroids.changed.add(this.changed.dispatch));
  }

  replaceSegments(oldValues: Uint64Set, newValues: Uint64Set) {
    const {source: {value: source}, target: {value: target}} = this;
    const newValue = newValues.size === 1 ? [...newValues][0] : undefined;
    const sourceChanged = !!source && oldValues.has(source.rootId);
    const targetChanged = !!target && oldValues.has(target.rootId);
    if (newValue) {
      if (sourceChanged) {
        source.rootId = newValue;
      }
      if (targetChanged) {
        target.rootId = newValue;
      }
      // don't want to fire off multiple changed
      if (sourceChanged || targetChanged) {
        if (this.centroids.value.length) {
          this.centroids.reset();
          this.triggerPathUpdate.dispatch();
        } else {
          this.changed.dispatch();
        }
      }
    } else {
      if (sourceChanged || targetChanged) {
        this.reset();
      }
    }
  }

  reset() {
    this.source.reset();
    this.target.reset();
    this.centroids.reset();
    this.precisionMode.reset();
  }

  toJSON() {
    const {
      source: {value: source},
      target: {value: target},
      centroids,
      precisionMode,
    } = this;
    return {
      [SOURCE_JSON_KEY]: source ? segmentSelectionToJSON(source) : undefined,
      [TARGET_JSON_KEY]: target ? segmentSelectionToJSON(target) : undefined,
      [CENTROIDS_JSON_KEY]: centroids.toJSON(),
      [PRECISION_MODE_JSON_KEY]: precisionMode.toJSON(), 
    };
  }

  restoreState(x: any) {
    verifyOptionalObjectProperty(
        x, SOURCE_JSON_KEY, value => {
          this.source.restoreState(restoreSegmentSelection(value));
        });
    verifyOptionalObjectProperty(
        x, TARGET_JSON_KEY, value => {
          this.target.restoreState(restoreSegmentSelection(value));
        });
    verifyOptionalObjectProperty(
        x, CENTROIDS_JSON_KEY, value => {
          this.centroids.restoreState(value);
        });
    verifyOptionalObjectProperty(
        x, PRECISION_MODE_JSON_KEY, value => {
          this.precisionMode.restoreState(value);
        });
  }
}

class MulticutState extends RefCounted implements Trackable {
  changed = new NullarySignal();

  sinks = new WatchableSet<SegmentSelection>();
  sources = new WatchableSet<SegmentSelection>();
  /** Pyr: true while the cut tool is open. Only then is the cell with the
   *  points kept on screen; closed, the player may hide it, and Pyr clears
   *  the points left on it when cut mode next opens. */
  toolActive = false;

  constructor(
      public focusSegment = new TrackableValue<Uint64|undefined>(undefined, x => x),
      public blueGroup = new WatchableValue<boolean>(false)) {
    super();

    const maybeResetFocusSegemnt = () => {
      if (this.sinks.size === 0 && this.sources.size === 0) {
        this.focusSegment.value = undefined;
      }
    };

    this.registerDisposer(focusSegment.changed.add(this.changed.dispatch));
    this.registerDisposer(this.sinks.changed.add(maybeResetFocusSegemnt));
    this.registerDisposer(this.sources.changed.add(maybeResetFocusSegemnt));

    this.registerDisposer(this.blueGroup.changed.add(this.changed.dispatch));
    this.registerDisposer(this.sinks.changed.add(this.changed.dispatch));
    this.registerDisposer(this.sources.changed.add(this.changed.dispatch));
  }

  replaceSegments(oldValues: Uint64Set, newValues: Uint64Set) {
    const newValue = newValues.size === 1 ? [...newValues][0] : undefined;
    const {focusSegment: {value: focusSegment}} = this;
    
    if (focusSegment && oldValues.has(focusSegment)) {
      if (newValue) {
        this.focusSegment.value = newValue;
        for (const sink of this.sinks) {
          sink.rootId = newValue;
        }
        for (const source of this.sources) {
          source.rootId = newValue;
        }
        this.changed.dispatch();
      } else {
        this.reset();
      }
    }
  }

  reset() {
    this.focusSegment.reset();
    this.blueGroup.value = false;
    this.sinks.clear();
    this.sources.clear();
  }

  toJSON() {
    const {focusSegment, sinks, sources} = this;
    return {
      [FOCUS_SEGMENT_JSON_KEY]: focusSegment.toJSON(),
      [SINKS_JSON_KEY]: [...sinks].map(segmentSelectionToJSON),
      [SOURCES_JSON_KEY]: [...sources].map(segmentSelectionToJSON),
    };
  }

  restoreState(x: any) {
    const segmentSelectionsValidator = (value: any) => {
      return parseArray(value, x => {
        return restoreSegmentSelection(x);
      });
    };

    verifyOptionalObjectProperty(x, FOCUS_SEGMENT_JSON_KEY, value => {
      this.focusSegment.restoreState(Uint64.parseString(String(value)));
    });
    const sinks = verifyObjectProperty(x, SINKS_JSON_KEY, segmentSelectionsValidator);
    const sources = verifyObjectProperty(x, SOURCES_JSON_KEY, segmentSelectionsValidator);

    for (const sink of sinks) {
      this.sinks.add(sink);
    }

    for (const source of sources) {
      this.sources.add(source);
    }
  }

  swapGroup() {
    this.blueGroup.value = !this.blueGroup.value;
  }

  get activeGroup() {
    return this.blueGroup.value ? this.sources : this.sinks;
  }

  // following three functions are used to render multicut supervoxels in 2d (color them red/blue)
  get segments() {
    return [...this.redSegments, ...this.blueSegments];
  }

  get redSegments() {
    return [...this.sinks].filter(x => !Uint64.equal(x.segmentId, x.rootId)).map(x => x.segmentId);
  }

  get blueSegments() {
    return [...this.sources]
        .filter(x => !Uint64.equal(x.segmentId, x.rootId))
        .map(x => x.segmentId);
  }
}

class GraphConnection extends SegmentationGraphSourceConnection {
  public annotationLayerStates: AnnotationLayerState[] = [];
  public mergeAnnotationState: AnnotationLayerState;
  public findPathAnnotationState: AnnotationLayerState;

  constructor(
      public graph: GrapheneGraphSource, private layer: SegmentationUserLayer,
      private chunkSource: GrapheneMultiscaleVolumeChunkSource, public state: GrapheneState) {
    super(graph, layer.displayState.segmentationGroupState.value);
    const segmentsState = layer.displayState.segmentationGroupState.value;
    this.registerDisposer(segmentsState.selectedSegments.changed.add((segmentIds: Uint64[]|Uint64|null, add: boolean) => {
      if (segmentIds !== null) {
        segmentIds = Array<Uint64>().concat(segmentIds);
      }
      this.selectedSegmentsChanged(segmentIds, add);
    }));

    this.registerDisposer(segmentsState.visibleSegments.changed.add((segmentIds: Uint64[]|Uint64|null, add: boolean) => {
      if (segmentIds !== null) {
        segmentIds = Array<Uint64>().concat(segmentIds);
      }
      this.visibleSegmentsChanged(segmentIds, add);
    }));

    const {annotationLayerStates, state: {multicutState, findPathState}} = this;
    const loadedSubsource = getGraphLoadedSubsource(layer)!;
    const redGroup = makeColoredAnnotationState(layer, loadedSubsource, 'sinks', RED_COLOR);
    const blueGroup = makeColoredAnnotationState(layer, loadedSubsource, 'sources', BLUE_COLOR);
    // EyeWire II: cut points have their own size (Celia #177).
    redGroup.displayState.ngeCutPoints = blueGroup.displayState.ngeCutPoints = true;
    synchronizeAnnotationSource(multicutState.sinks, redGroup);
    synchronizeAnnotationSource(multicutState.sources, blueGroup);
    annotationLayerStates.push(redGroup, blueGroup);

    if (layer.tool.value instanceof MergeSegmentsPlaceLineTool) {
      layer.tool.value = undefined;
    }

    this.mergeAnnotationState = makeColoredAnnotationState(layer, loadedSubsource, "grapheneMerge", RED_COLOR);

    {
      const {mergeState} = state;
      const {merges, autoSubmit} = mergeState;
      const {mergeAnnotationState} = this;
      const {visibleSegments} = segmentsState;


      // load merges from state
      for (const merge of merges.value) {
        mergeAnnotationState.source.add(mergeToLine(merge));
      }

      // initialize source changes
      this.registerDisposer(mergeAnnotationState.source.childAdded.add((x) => {
        const annotation = x as Line;
        const relatedSegments = annotation.relatedSegments![0];
        const visibles = relatedSegments.map(x => visibleSegments.has(x));
        if (visibles[0] === false) {
          setTimeout(() => {
            const {tool} = layer;
            if (tool.value instanceof MergeSegmentsPlaceLineTool) {
              tool.value.deactivate();
            }
          }, 0);
          StatusMessage.showTemporaryMessage(`Cannot merge a hidden segment.`);
        } else if (merges.value.length < MAX_MERGE_COUNT) {
          merges.value = [...merges.value, lineToSubmission(annotation, true)];
        } else {
          setTimeout(() => {
            const {tool} = layer;
            if (tool.value instanceof MergeSegmentsPlaceLineTool) {
              tool.value.deactivate();
            }
          }, 0);
          StatusMessage.showTemporaryMessage(`Maximum of ${MAX_MERGE_COUNT} simultanous merges allowed.`);
        }
      }));

      this.registerDisposer(mergeAnnotationState.source.childCommitted.add((x) => {
        const ref = mergeAnnotationState.source.getReference(x);
        const annotation = ref.value as Line|undefined;
        if (annotation) {
          const relatedSegments = annotation.relatedSegments![0];
          const visibles = relatedSegments.map(x => visibleSegments.has(x));
          if (relatedSegments.length < 4) {
            mergeAnnotationState.source.delete(ref);
            StatusMessage.showTemporaryMessage(`Cannot merge segment with itself.`);
          }
          if (visibles[2] === false) {
            mergeAnnotationState.source.delete(ref);
            StatusMessage.showTemporaryMessage(`Cannot merge a hidden segment.`);
          }
          const existingSubmission = merges.value.find(x => x.id === ref.id);
          if (existingSubmission && !existingSubmission?.locked) { //  how would it be locked?
            const newSubmission = lineToSubmission(annotation, false);
            existingSubmission.sink = newSubmission.sink;
            existingSubmission.source = newSubmission.source;
            merges.changed.dispatch();
            if (autoSubmit.value) {
              this.bulkMerge([existingSubmission]);
            }
          }
        }
        ref.dispose();
      }));

      this.registerDisposer(mergeAnnotationState.source.childDeleted.add((id) => {
        let changed = false;
        const filtered = merges.value.filter(x => {
          const keep = x.id !== id || x.locked;
          if (!keep) {
            changed = true;
          }
          return keep;
        });
        if (changed) {
          merges.value = filtered;
        }
      }));
    }

    const findPathGroup = makeColoredAnnotationState(layer, loadedSubsource, "findpath", WHITE_COLOR, false);
    this.findPathAnnotationState = findPathGroup;
    findPathGroup.source.childDeleted.add(annotationId => {
      if (findPathState.source.value?.annotationReference?.id === annotationId) {
        findPathState.source.value = undefined;
      }
      if (findPathState.target.value?.annotationReference?.id === annotationId) {
        findPathState.target.value = undefined;
      }
    });
    const findPathChanged = () => {
      const {path, source, target} = findPathState;
      const annotationSource = findPathGroup.source;
      if (source.value && !source.value.annotationReference) {
        addSelection(annotationSource, source.value, "source");
      }
      if (target.value && !target.value.annotationReference) {
        addSelection(annotationSource, target.value, "target");
      }
      for (const annotation of annotationSource) {
        if (annotation.id !== source.value?.annotationReference?.id &&
            annotation.id !== target.value?.annotationReference?.id) {
          annotationSource.delete(annotationSource.getReference(annotation.id));
        }
      }
      for (const line of path) {
        // line.id = ''; // TODO, is it a bug that this is necessary? annotationMap is empty if I step through it but logging shows it isn't empty
        annotationSource.add(line);
      }
    };
    this.registerDisposer(findPathState.changed.add(findPathChanged));
    this.registerDisposer(findPathState.triggerPathUpdate.add(() => {
      const loadedSubsource = getGraphLoadedSubsource(this.layer)!;
      const annotationToNanometers = loadedSubsource.loadedDataSource.transform.inputSpace.value.scales.map(x => x / 1e-9);
      this.submitFindPath(findPathState.precisionMode.value, annotationToNanometers).then(success => {
        success;
      });
    }));
    findPathChanged(); // initial state
  }

  createRenderLayers(
      chunkManager: ChunkManager, displayState: SegmentationDisplayState3D,
      localPosition: WatchableValueInterface<Float32Array>): RenderLayer[] {
    return [new SliceViewPanelChunkedGraphLayer(
        chunkManager,
        this.chunkSource.getChunkedGraphSource(),
        displayState,  // FIXME will displayState always match this.segmentsState?
        localPosition,
        this.graph.info.graph.nBitsForLayerId,
        )];
  };

  private lastDeselectionMessage: StatusMessage|undefined;
  private lastDeselectionMessageExists = false;

  private visibleSegmentsChanged(segments: Uint64[]|null, added: boolean) {
    const {segmentsState} = this;
    const {focusSegment: {value: focusSegment}, toolActive} = this.graph.state.multicutState;
    if (toolActive && focusSegment && !segmentsState.visibleSegments.has(focusSegment)) {
      if (segmentsState.selectedSegments.has(focusSegment)) {
        StatusMessage.showTemporaryMessage(`Can't hide active multicut segment.`, 3000);
      } else {
        StatusMessage.showTemporaryMessage(`Can't deselect active multicut segment.`, 3000);
      }
      segmentsState.selectedSegments.add(focusSegment);
      segmentsState.visibleSegments.add(focusSegment);
      if (segments) {
        segments = segments.filter(segment => !Uint64.equal(segment, focusSegment));
      }
    }
    if (segments === null) {
      const leafSegmentCount = this.segmentsState.selectedSegments.size;
      this.segmentsState.segmentEquivalences.clear();
      StatusMessage.showTemporaryMessage(`Hid all ${leafSegmentCount} segments.`, 3000);
      return;
    }
    for (const segmentId of segments) {
      if (!added) {
        const segmentCount = [...segmentsState.segmentEquivalences.setElements(segmentId)].length; // Approximation
        segmentsState.segmentEquivalences.deleteSet(segmentId);
        // nge: no "Hid N segments." bar for this. It ran along the bottom of
        // the screen for two seconds after every single removal, so the
        // legend, the chat and the coordinates all jumped up and back down
        // each time (Nseraf 2026-10-12, report 121). The row leaving the list
        // already says the segment is gone, and Undelete brings it back.
        void segmentCount;
      }
    }
  }

  private selectedSegmentsChanged(segments: Uint64[]|null, added: boolean) {
    const {segmentsState} = this;
    if (segments === null) {
      const leafSegmentCount = this.segmentsState.selectedSegments.size;
      StatusMessage.showTemporaryMessage(`Deselected all ${leafSegmentCount} segments.`, 3000);
      return;
    }
    for (const segmentId of segments) {
      const isBaseSegment = isBaseSegmentId(segmentId, this.graph.info.graph.nBitsForLayerId);
      const segmentConst = segmentId.clone();
      if (added && isBaseSegment) {
        this.graph.getRoot(segmentConst).then(rootId => {
          if (segmentsState.visibleSegments.has(segmentConst)) {
            segmentsState.visibleSegments.add(rootId);
          }
          segmentsState.selectedSegments.delete(segmentConst);
          segmentsState.selectedSegments.add(rootId);
        });
      }
    }
  }

  computeSplit(include: Uint64, exclude: Uint64): ComputedSplit|undefined {
    include;
    exclude;
    return undefined;
  }

  getMeshSource() {
    const {layer} = this;
    for (const x of layer.renderLayers) {
      if (x instanceof MeshLayer || x instanceof MultiscaleMeshLayer) {
        return x.source;
      }
    }
    return undefined;
  }

  meshAddNewSegments(segments: Uint64[]) {
    const meshSource = this.getMeshSource();
    if (meshSource) {
      for (const segment of segments) {
        meshSource.rpc!.invoke(
          GRAPHENE_MESH_NEW_SEGMENT_RPC_ID,
          {'rpcId': meshSource.rpcId!, 'segment': segment.toString()});
      }
    }
  }

  async submitFindPath(precisionMode: boolean, annotationToNanometers: Float64Array): Promise<boolean> {
    const {state: {findPathState}} = this;
    const {source, target} = findPathState;
    if (!source.value || !target.value) return false;
    // nge: one search at a time; closing Find Path stops it (ngeStopFindPath),
    // and a search that runs too long gives up and says why (Ames
    // 2026-10-24: stuck at 3 minutes on MEC with Precision mode, still going
    // after the tool was closed).
    ngeFindPathRun?.cancel();
    const run = ngeFindPathRun = new CancellationTokenSource();
    let tooSlow = false;
    const timer = setTimeout(() => { tooSlow = true; run.cancel(); }, (precisionMode ? 90 : 60) * 1000);
    try {
      const centroids = await this.graph.graphServer.findPath(source.value, target.value, precisionMode, annotationToNanometers, run);
      StatusMessage.showTemporaryMessage('Path found!', 5000);
      findPathState.centroids.value = centroids;
      return true;
    } catch (e) {
      if (run.isCanceled) {
        if (tooSlow) {
          StatusMessage.showTemporaryMessage(precisionMode
              ? 'Path finding failed: the server took too long. Turn Precision mode off and try again, or pick two closer points.'
              : 'Path finding failed: the server took too long. Try two closer points.', 10000);
        }
        return false;
      }
      throw e;
    } finally {
      clearTimeout(timer);
      if (ngeFindPathRun === run) ngeFindPathRun = undefined;
    }
  }

  async submitMulticut(annotationToNanometers: Float64Array): Promise<boolean> {
    const {state: {multicutState}} = this;
    const {sinks, sources} = multicutState;
    if (sinks.size === 0 || sources.size === 0) {
      StatusMessage.showTemporaryMessage(
          'Must select both red and blue groups to perform a multi-cut.', 7000);
      return false;
    } else {
      const splitRoots = await this.graph.graphServer.splitSegments(
          [...sinks], [...sources], annotationToNanometers);
      if (splitRoots.length === 0) {
        StatusMessage.showTemporaryMessage(`No split found.`, 3000);
        return false;
      } else {
        const focusSegment = multicutState.focusSegment.value!;
        multicutState
            .reset();  // need to clear the focus segment before deleting the multicut segment
        const {segmentsState} = this;
        segmentsState.selectedSegments.delete(focusSegment);
        for (const segment of [...sinks, ...sources]) {
          segmentsState.selectedSegments.delete(segment.rootId);
        }
        this.meshAddNewSegments(splitRoots);
        segmentsState.selectedSegments.add(splitRoots);
        segmentsState.visibleSegments.add(splitRoots);
        const oldValues = new Uint64Set();
        oldValues.add(focusSegment);
        const newValues = new Uint64Set();
        newValues.add(splitRoots);
        this.state.replaceSegments(oldValues, newValues);
        return true;
      }
    }
  }

  deleteMergeSubmission = (submission: MergeSubmission) => {
    const {mergeAnnotationState} = this;
    submission.locked = false;
    // It may already be gone (Clear can now remove one that is still being
    // sent, and a finished one removes itself a few seconds later).
    const ref = mergeAnnotationState.source.getReference(submission.id);
    try { if (ref.value) mergeAnnotationState.source.delete(ref); } finally { ref.dispose(); }
  }

  private submitMerge = async (submission: MergeSubmission, attempts=1): Promise<Uint64> => {
    const loadedSubsource = getGraphLoadedSubsource(this.layer)!;
    const annotationToNanometers = loadedSubsource.loadedDataSource.transform.inputSpace.value.scales.map(x => x / 1e-9);
    submission.error = undefined;
    // nge: a busy server answers "try later" and the request layer keeps
    // trying, quietly, for minutes. The merge stayed locked on "trying..."
    // all that time and Clear could not remove it (Nseraf 2026-10-09). Each
    // merge now gives up after MERGE_WAIT_MS, and Clear can stop one early.
    const stop = new CancellationTokenSource();
    submission.cancel = (why: string) => { submission.error = why; stop.cancel(); };
    const limit = setTimeout(() => submission.cancel?.('The server did not answer in time. Nothing was merged as far as the game can tell; check the cell, then try again.'), MERGE_WAIT_MS);
    try {
    for (let i = 1; i <= attempts; i++) {
      try {
        if (stop.isCanceled) throw new Error(submission.error || 'Stopped.');
        const newRoot = await this.graph.graphServer.mergeSegments(submission.sink, submission.source!, annotationToNanometers, stop);
        const oldValues = new Uint64Set();
        oldValues.add(submission.sink.rootId);
        oldValues.add(submission.source!.rootId);
        const newValues = new Uint64Set();
        newValues.add(newRoot);
        this.state.replaceSegments(oldValues, newValues);
        return newRoot;
      } catch (err) {
        if (i === attempts || stop.isCanceled) {
          // A stop set its own reason: keep it over the bare "cancelled".
          if (!stop.isCanceled || !submission.error) submission.error = err.message || "unknown";
          throw err;
        }
      }
    }
    } finally {
      clearTimeout(limit);
      submission.cancel = undefined;
    }

    return Uint64.ZERO; // appease typescript
  }

  async bulkMerge(submissions: MergeSubmission[]) {
      const {merges} = this.state.mergeState;
      const bulkMergeHelper = (submissions: MergeSubmission[]): Promise<Uint64[]> => {
        return new Promise(f => {
          if (submissions.length === 0) {
            f([]);
            return;
          }
          const segmentsToRemove: Uint64[] = [];
          let completed = 0;
          let activeLoops = 0;
          const loop = (completedAt: number, pending: MergeSubmission[]) => {
            if (completed === submissions.length || pending.length === 0) return;
            activeLoops++;
            let failed: MergeSubmission[] = [];
            const checkDone = () => {
              loopDone++;
              if (loopDone === pending.length) {
                activeLoops -= 1;
              }
              if (activeLoops === 0) {
                f(segmentsToRemove);
              }
            };
            let loopDone = 0;
            for (const submission of pending) {
              submission.locked = true;
              submission.status = 'trying...';
              merges.changed.dispatch();
              const segments = [submission.source!.rootId, submission.sink.rootId];
              this.submitMerge(submission, 3).then(mergedRoot => {
                segmentsToRemove.push(...segments);
                submission.status = 'done';
                submission.mergedRoot = mergedRoot;
                merges.changed.dispatch();
                completed += 1;
                loop(completed, failed);
                failed = [];
                checkDone();
                wait(5000).then(() => {
                  this.deleteMergeSubmission(submission);
                });
              }).catch(() => {
                merges.changed.dispatch();
                // One the player cleared is not tried again.
                if (submission.cleared) { checkDone(); return; }
                failed.push(submission);
                if (completed > completedAt) {
                  loop(completed, failed);
                  failed = [];
                }
                checkDone();
              });
            }
          };
          loop(completed, submissions);
        });
      };

      submissions = submissions.filter(x => !x.locked && x.source);
      // nge: every ID these merges touched, as sent. A failed merge (the same
      // two pieces merged twice) left its old ID in the segment list, out of
      // date (Nseraf, report 349); Pyr swaps such IDs for the current one.
      const touched = new Set<string>();
      for (const s of submissions) { touched.add(s.sink.rootId.toString()); touched.add(s.source!.rootId.toString()); }
      const segmentsToRemove = await bulkMergeHelper(submissions);
      const segmentsToAdd: Uint64[] = [];
      for (const submission of submissions) {
        if (submission.error) {
          submission.locked = false;
          submission.status = submission.error;
        } else if (submission.mergedRoot) {
          segmentsToAdd.push(submission.mergedRoot);
        }
      }
      const latestRoots = await this.graph.graphServer.filterLatestRoots(segmentsToAdd);
      const segmentsState = this.layer.displayState.segmentationGroupState.value;
      const {visibleSegments, selectedSegments} = segmentsState;
      selectedSegments.delete(segmentsToRemove);
      this.meshAddNewSegments(latestRoots);
      selectedSegments.add(latestRoots);
      visibleSegments.add(latestRoots);
      merges.changed.dispatch();
      try {
        for (const s of submissions) { touched.add(s.sink.rootId.toString()); if (s.source) touched.add(s.source.rootId.toString()); }
        window.dispatchEvent(new CustomEvent('nge-merge-batch-done', {detail: {roots: [...touched]}}));
      } catch { /* tidying is never worth breaking a merge for */ }
    }
}

async function parseGrapheneError(e: HttpError) {
  if (e.response) {
    let msg: string;
    if (e.response.headers.get('content-type') === 'application/json') {
      msg = (await e.response.json())['message'];
    } else {
      msg = await e.response.text();
    }
    return msg;
  }
  return undefined;
}

async function withErrorMessageHTTP(promise: Promise<Response>, options: {
    initialMessage?: string,
    errorPrefix: string,
  }): Promise<Response> {
    let status: StatusMessage|undefined = undefined;
    let dispose = () => {};
    if (options.initialMessage) {
      status = new StatusMessage(true);
      status.setText(options.initialMessage);
      dispose = status.dispose.bind(status);
    }
    try {
      const response = await promise;
      dispose();
      return response;
    } catch (e) {
      if (e instanceof HttpError && e.response) {
        const {errorPrefix = ''} = options;
        const msg = await parseGrapheneError(e) || 'unknown error';
        if (!status) {
          status = new StatusMessage(true);
        }
        status.setErrorMessage(errorPrefix + msg);
        status.setVisible(true);
        throw new Error(`[${e.response.status}] ${errorPrefix}${msg}`);
      }
      // nge: a request that was stopped (Find Path closed, or too slow) or
      // never reached the server took its "Finding path" line with it; it
      // stayed and kept counting (Ames 2026-10-24).
      dispose();
      throw e;
    }
}

export const GRAPH_SERVER_NOT_SPECIFIED = Symbol('Graph Server Not Specified.');

/** nge: the Find Path tool's search under way, if any. */
let ngeFindPathRun: CancellationTokenSource|undefined;
export function ngeStopFindPath() {
  ngeFindPathRun?.cancel();
  ngeFindPathRun = undefined;
}


/**
 * EyeWire II: tell the app that the graph server has acknowledged a split or
 * a merge, with the operation id it gave it. The app counts edits from these
 * (one acknowledged operation is one edit) instead of inferring them from the
 * visible segments changing, and the server can look the id up.
 */
function ngeGraphEdit(kind: 'merge'|'split', graph: string, jsonResp: any) {
  try {
    const id = jsonResp?.['operation_id'];
    window.dispatchEvent(new CustomEvent('nge-graph-edit', {
      detail: {
        kind,
        graph,
        operationId: id == null ? null : String(id),
        roots: Array.isArray(jsonResp?.['new_root_ids']) ? jsonResp['new_root_ids'].map(String) : [],
      }
    }));
  } catch { /* counting is never worth breaking an edit for */ }
}
/**
 * nge: Pyr can refuse an edit before it is sent. The Hunt freezes its cells
 * while it runs, so a merge or cut that touches one stops here, with the
 * reason in the status bar. The app sets window.__ngeEditGuard(kind, roots),
 * which answers a sentence to refuse or null to let the edit through.
 */
function ngeEditRefusal(kind: 'merge'|'split', roots: Uint64[], edit?: any): string|null {
  try {
    const guard = (window as any)['__ngeEditGuard'];
    if (typeof guard !== 'function') return null;
    // The edit itself, as it would be sent, so a Hunt Builder helper's merge or cut can be kept as a
    // suggestion instead (src/hunt_build.ts): a merge [[sv, x, y, z], [sv, x, y, z]], a cut {sources, sinks}, nm.
    const why = guard(kind, roots.map(r => String(r)), edit);
    return typeof why === 'string' && why ? why : null;
  } catch {
    return null;  // a broken guard never blocks an edit
  }
}
function ngeRefuseEdit(kind: 'merge'|'split', roots: Uint64[], edit?: any) {
  const why = ngeEditRefusal(kind, roots, edit);
  if (!why) return;
  StatusMessage.showTemporaryMessage(why, 8000);
  throw new Error(why);
}
class GrapheneGraphServerInterface {
  constructor(
      private url: string, private credentialsProvider: SpecialProtocolCredentialsProvider) {}

  async getRoot(segment: Uint64, timestamp = '') {
    const timestampEpoch = (new Date(timestamp)).valueOf() / 1000;

    const url = `${this.url}/node/${String(segment)}/root?int64_as_str=1${
        Number.isNaN(timestampEpoch) ? '' : `&timestamp=${timestampEpoch}`}`

    const promise = cancellableFetchSpecialOk(this.credentialsProvider, url, {}, responseIdentity);

    const response = await withErrorMessageHTTP(promise, {
      initialMessage: `Retrieving root for segment ${segment}`,
      errorPrefix: `Could not fetch root: `
    });
    const jsonResp = await response.json();
    return Uint64.parseString(jsonResp['root_id']);
  }

  async mergeSegments(
      first: SegmentSelection, second: SegmentSelection,
      annotationToNanometers: Float64Array, cancellationToken?: CancellationToken): Promise<Uint64> {
    const {url} = this;
    if (url === '') {
      return Promise.reject(GRAPH_SERVER_NOT_SPECIFIED);
    }
    const mergeBody = [
      [
        String(first.segmentId),
        ...first.position.map((val, i) => val * annotationToNanometers[i])
      ],
      [
        String(second.segmentId),
        ...second.position.map((val, i) => val * annotationToNanometers[i])
      ]
    ];
    ngeRefuseEdit('merge', [first.rootId, second.rootId], mergeBody);

    const promise = cancellableFetchSpecialOk(
        this.credentialsProvider, `${url}/merge?int64_as_str=1`, {
          method: 'POST',
          body: JSON.stringify(mergeBody)
        },
        responseIdentity, cancellationToken);

    try {
      const response = await promise;
      const jsonResp = await response.json();
      ngeGraphEdit('merge', url, jsonResp);
      return Uint64.parseString(jsonResp['new_root_ids'][0]);
    } catch (e) {
      if (e instanceof HttpError) {
        const msg = await parseGrapheneError(e);
        throw new Error(msg);
      }
      throw e;
    }
  }

  async splitSegments(
      first: SegmentSelection[], second: SegmentSelection[],
      annotationToNanometers: Float64Array): Promise<Uint64[]> {
    const {url} = this;
    if (url === '') {
      return Promise.reject(GRAPH_SERVER_NOT_SPECIFIED);
    }
    const splitBody = {
      'sources': first.map(
          x =>
              [String(x.segmentId),
               ...x.position.map((val, i) => val * annotationToNanometers[i])]),
      'sinks': second.map(
          x =>
              [String(x.segmentId),
               ...x.position.map((val, i) => val * annotationToNanometers[i])])
    };
    ngeRefuseEdit('split', [...first, ...second].map(x => x.rootId), splitBody);

    const promise =
        cancellableFetchSpecialOk(
            this.credentialsProvider, `${url}/split?int64_as_str=1`, {
              method: 'POST',
              body: JSON.stringify(splitBody)
            },
            responseIdentity);

    const response = await withErrorMessageHTTP(promise, {
      initialMessage: `Splitting ${first.length} sources from ${second.length} sinks`,
      errorPrefix: 'Split failed: '
    });
    const jsonResp = await response.json();
    ngeGraphEdit('split', url, jsonResp);
    const final: Uint64[] = new Array(jsonResp['new_root_ids'].length);
    for (let i = 0; i < final.length; ++i) {
      final[i] = Uint64.parseString(jsonResp['new_root_ids'][i]);
    }
    return final;
  }

  async filterLatestRoots(segments: Uint64[]): Promise<Uint64[]> {
    const url = `${this.url}/is_latest_roots`;

    const promise = cancellableFetchSpecialOk(this.credentialsProvider, url, {
      method: 'POST',
      body: JSON.stringify({
        "node_ids": segments.map(x => x.toJSON())
      }),
    }, responseIdentity);

    const response = await withErrorMessageHTTP(promise, {
      errorPrefix: `Could not check latest: `
    });
    const jsonResp = await response.json();

    const res: Uint64[] = [];
    for (const [i, isLatest] of jsonResp['is_latest'].entries()) {
      if (isLatest) {
        res.push(segments[i]);
      }
    }
    return res;
  }

  async findPath(first: SegmentSelection, second: SegmentSelection, precisionMode: boolean, annotationToNanometers: Float64Array,
      cancellationToken: CancellationToken = uncancelableToken):
      Promise<number[][]> {
    const {url} = this;
    if (url === '') {
      return Promise.reject(GRAPH_SERVER_NOT_SPECIFIED);
    }

    const promise =
        cancellableFetchSpecialOk(this.credentialsProvider, `${url}/graph/find_path?int64_as_str=1&precision_mode=${Number(precisionMode)}`, {
          method: 'POST',
          body: JSON.stringify([
            [String(first.rootId), ...first.position.map((val, i) => val * annotationToNanometers[i])],
            [String(second.rootId), ...second.position.map((val, i) => val * annotationToNanometers[i])],
          ]),
        }, responseIdentity, cancellationToken);

    const response = await withErrorMessageHTTP(promise, {
      initialMessage: `Finding path between ${first.segmentId} and ${second.segmentId}`,
      errorPrefix: 'Path finding failed: '
    });
    const jsonResponse = await response.json();
    const supervoxelCentroidsKey = 'centroids_list';
    const centroids = jsonResponse[supervoxelCentroidsKey];

    const centroidsTransformed = centroids.map((point: number[]) => {
      return point.map((val, i) => val / annotationToNanometers[i]);
    });

    const missingL2IdsKey = 'failed_l2_ids';
    const missingL2Ids = jsonResponse[missingL2IdsKey];
    if (missingL2Ids && missingL2Ids.length > 0) {
      StatusMessage.showTemporaryMessage(
          'Some level 2 meshes are missing, so the path shown may have a poor level of detail.');
    }
    return centroidsTransformed;
  }

  // ── EyeWire II Highlight: "toward the soma" and "beyond this point" ──
  // A cell is a graph of small pieces (level 2 chunks). The server gives the
  // links between a cell's pieces, and a point inside each piece, which is
  // all it takes to know what lies on which side of a click.

  /** What a cut WOULD do, without making it: the smallest bits of the cell
   *  (supervoxels) that would end up on each side, near the points. Same
   *  request as the cut itself; the first group given comes back first.
   *  `illegal` is the server saying these points cannot be cut apart. */
  async ngeSplitPreview(
      first: SegmentSelection[], second: SegmentSelection[],
      annotationToNanometers: Float64Array): Promise<{sides: string[][], illegal: boolean}> {
    const {url} = this;
    if (url === '') return Promise.reject(GRAPH_SERVER_NOT_SPECIFIED);
    const row = (x: SegmentSelection) =>
        [String(x.segmentId), ...x.position.map((val, i) => val * annotationToNanometers[i])];
    let response: Response;
    try {
      response = await cancellableFetchSpecialOk(
          this.credentialsProvider, `${url}/graph/split_preview?int64_as_str=1`,
          {method: 'POST', body: JSON.stringify({'sources': first.map(row), 'sinks': second.map(row)})},
          responseIdentity);
    } catch (e) {
      // The server says why in its answer (points that cannot be told apart,
      // a point off the cell...): pass that on, not just the error number.
      if (e instanceof HttpError && e.response) {
        let why: string|undefined;
        try { why = await parseGrapheneError(e); } catch { why = undefined; }
        const text = String(why ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
        throw new Error(text ? `the server said "${text}" (error ${e.status})` : `the server refused it (error ${e.status})`);
      }
      throw e;
    }
    // The ids are larger than a JavaScript number holds. If the server sent
    // them as bare numbers, quote them before the text is read as JSON.
    const text = (await response.text()).replace(/([\[,:\s])(\d{16,})(?=[\s,\]}])/g, '$1"$2"');
    const json = JSON.parse(text);
    const groups = json['supervoxel_connected_components'];
    if (!Array.isArray(groups)) throw new Error('The server did not return a preview of this cut.');
    return {
      sides: groups.map((g: any) => Array.isArray(g) ? g.map(String) : []),
      illegal: json['illegal_split'] === true,
    };
  }

  /** The links between the pieces of a cell, as pairs of piece ids. */
  async ngeBranchEdges(rootId: string): Promise<[string, string][]> {
    const {url} = this;
    if (url === '') return Promise.reject(GRAPH_SERVER_NOT_SPECIFIED);
    const response = await cancellableFetchSpecialOk(
        this.credentialsProvider, `${url}/node/${rootId}/lvl2_graph?int64_as_str=1`, {}, responseIdentity);
    const edges = (await response.json())['edge_graph'];
    if (!Array.isArray(edges)) throw new Error('The server did not return this cell\'s branches.');
    return edges.map((e: any[]) => [String(e[0]), String(e[1])] as [string, string]);
  }

  /** The piece of the cell that a supervoxel belongs to. */
  async ngePieceOf(supervoxelId: string): Promise<string> {
    const {url} = this;
    if (url === '') return Promise.reject(GRAPH_SERVER_NOT_SPECIFIED);
    const response = await cancellableFetchSpecialOk(
        this.credentialsProvider, `${url}/node/${supervoxelId}/root?int64_as_str=1&stop_layer=2`, {}, responseIdentity);
    return String((await response.json())['root_id']);
  }

  /** A point inside each piece, in NANOMETERS. From the server's store of
   *  piece positions where the dataset has one (the retina does). Where it
   *  has none (the Sandbox), or for any piece it is missing, the middle of
   *  the piece's chunk of the volume, which the piece's own id spells out:
   *  coarser, a point every few microns, but always there. */
  async ngePiecePoints(ids: string[]): Promise<Map<string, number[]>> {
    const {url} = this;
    // .../segmentation/api/v1/table/<name>  ->  .../l2cache/api/v1/table/<name>
    const m = url.match(/^(https:\/\/[^/]+)\/segmentation\/(?:api\/v1\/)?table\/([^/?#]+)/);
    if (!m) throw new Error('This dataset has no map of its branches.');
    const out = new Map<string, number[]>();
    const wanted = ids.filter(id => /^\d+$/.test(id));
    // A dataset with no store of piece positions answers with an error every
    // time (the Sandbox): ask it once, not on every use.
    const storeKey = `${m[1]}/${m[2]}`;
    try {
      if (this.ngeNoPiecePoints.has(storeKey)) throw new Error('known to have none');
      const BATCH = 2000;
      for (let i = 0; i < wanted.length; i += BATCH) {
        const batch = wanted.slice(i, i + BATCH);
        // The ids are larger than a JavaScript number holds: written as text.
        const response = await cancellableFetchSpecialOk(
            this.credentialsProvider,
            `${m[1]}/l2cache/api/v1/table/${m[2]}/attributes?attribute_names=rep_coord_nm&int64_as_str=1`,
            {method: 'POST', headers: {'Content-Type': 'application/json'}, body: `{"l2_ids":[${batch.join(',')}]}`},
            responseIdentity);
        const json = await response.json();
        for (const id of batch) {
          const pt = json?.[id]?.['rep_coord_nm'];
          if (Array.isArray(pt) && pt.length === 3 && pt.every((v: any) => Number.isFinite(v))) out.set(id, pt.map(Number));
        }
      }
    } catch (e) {
      if (!this.ngeNoPiecePoints.has(storeKey)) console.info('[highlight] no stored piece positions for this dataset, using chunk middles:', (e as any)?.message);
      this.ngeNoPiecePoints.add(storeKey);
    }
    if (out.size < wanted.length) {
      const middle = await this.ngeChunkMiddle(m[1], m[2]);
      for (const id of wanted) if (!out.has(id)) { const p = middle(id); if (p) out.set(id, p); }
    }
    return out;
  }

  private ngeNoPiecePoints = new Set<string>();

  /** How thick the cell is in each piece: the distance, in NANOMETERS, from
   *  the piece's innermost point to its surface (the server's max_dt_nm).
   *  The soma is by far the thickest part of a cell. Empty where the
   *  dataset keeps no such store (the Sandbox). */
  async ngePieceThickness(ids: string[]): Promise<Map<string, number>> {
    const out = new Map<string, number>();
    const m = this.url.match(/^(https:\/\/[^/]+)\/segmentation\/(?:api\/v1\/)?table\/([^/?#]+)/);
    if (!m || this.ngeNoPiecePoints.has(`${m[1]}/${m[2]}`)) return out;
    const wanted = ids.filter(id => /^\d+$/.test(id));
    try {
      const BATCH = 2000;
      for (let i = 0; i < wanted.length; i += BATCH) {
        const batch = wanted.slice(i, i + BATCH);
        const response = await cancellableFetchSpecialOk(
            this.credentialsProvider,
            `${m[1]}/l2cache/api/v1/table/${m[2]}/attributes?attribute_names=max_dt_nm&int64_as_str=1`,
            {method: 'POST', headers: {'Content-Type': 'application/json'}, body: `{"l2_ids":[${batch.join(',')}]}`},
            responseIdentity);
        const json = await response.json();
        for (const id of batch) {
          const v = Number(json?.[id]?.['max_dt_nm']);
          if (Number.isFinite(v) && v > 0) out.set(id, v);
        }
      }
    } catch { /* none: the caller guesses another way */ }
    return out;
  }

  /** The block of the volume each piece lies in, in NANOMETERS, as
   *  [x0, y0, z0, x1, y1, z1]. Read from the piece's id, so it asks the
   *  server nothing beyond the dataset's own description (asked once). */
  async ngeChunkBoxes(ids: string[]): Promise<Map<string, number[]>> {
    const {url} = this;
    const m = url.match(/^(https:\/\/[^/]+)\/segmentation\/(?:api\/v1\/)?table\/([^/?#]+)/);
    if (!m) throw new Error('This dataset has no map of its branches.');
    const middle = await this.ngeChunkMiddle(m[1], m[2]);
    const half = this.ngeChunkHalf.get(`${m[1]}/${m[2]}`);
    const out = new Map<string, number[]>();
    if (!half) return out;
    for (const id of ids) {
      const p = middle(id);
      if (p) out.set(id, [p[0] - half[0], p[1] - half[1], p[2] - half[2], p[0] + half[0], p[1] + half[1], p[2] + half[2]]);
    }
    return out;
  }
  /** Half the size of a level 2 chunk, in nanometers, per dataset. */
  private ngeChunkHalf = new Map<string, number[]>();

  /** Where a piece's chunk of the volume is, read from the piece's id. A
   *  level 2 id is, from the top bit down: the layer, then the chunk's x, y
   *  and z, then a number within the chunk. */
  private ngeChunkInfo = new Map<string, Promise<(id: string) => number[]|undefined>>();
  ngeChunkMiddle(host: string, table: string): Promise<(id: string) => number[]|undefined> {
    const key = `${host}/${table}`;
    let made = this.ngeChunkInfo.get(key);
    if (!made) {
      made = (async () => {
        const response = await cancellableFetchSpecialOk(
            this.credentialsProvider, `${host}/segmentation/table/${table}/info`, {}, responseIdentity);
        const info = await response.json();
        const graph = info?.['graph'], scale = info?.['scales']?.[0];
        const layerBits = Number(graph?.['n_bits_for_layer_id']);
        const bits = Number(graph?.['spatial_bit_masks']?.['2']);
        // Supervoxels (layer 1) sit in the same blocks as the pieces (layer 2).
        const bits1 = Number(graph?.['spatial_bit_masks']?.['1']);
        const chunk: number[] = graph?.['chunk_size'], res: number[] = scale?.['resolution'];
        if (!Number.isFinite(layerBits) || !Number.isFinite(bits) || !Array.isArray(chunk) || !Array.isArray(res)) {
          throw new Error('This dataset has no map of its branches.');
        }
        const offset: number[] = info?.['chunks_start_at_voxel_offset'] && Array.isArray(scale?.['voxel_offset']) ? scale['voxel_offset'] : [0, 0, 0];
        const mask = (BigInt(1) << BigInt(bits)) - BigInt(1);
        this.ngeChunkHalf.set(key, [0, 1, 2].map(k => chunk[k] * res[k] / 2));
        return (id: string) => {
          let n: bigint;
          try { n = BigInt(id); } catch { return undefined; }
          const layer = Number(n >> BigInt(64 - layerBits));
          if (layer !== 2 && !(layer === 1 && bits1 === bits)) return undefined;
          const at = (k: number) => Number((n >> BigInt(64 - layerBits - bits * (k + 1))) & mask);
          return [0, 1, 2].map(k => (offset[k] + (at(k) + 0.5) * chunk[k]) * res[k]);
        };
      })();
      this.ngeChunkInfo.set(key, made);
      made.catch(() => this.ngeChunkInfo.delete(key));
    }
    return made;
  }
}

class GrapheneGraphSource extends SegmentationGraphSource {
  private connections = new Set<GraphConnection>();
  public graphServer: GrapheneGraphServerInterface;

  constructor(
      public info: GrapheneMultiscaleVolumeInfo,
      credentialsProvider: SpecialProtocolCredentialsProvider,
      private chunkSource: GrapheneMultiscaleVolumeChunkSource, public state: GrapheneState) {
    super();
    this.graphServer =
        new GrapheneGraphServerInterface(info.app!.segmentationUrl, credentialsProvider);
  }

  connect(layer: SegmentationUserLayer): Owned<SegmentationGraphSourceConnection> {
    const connection = new GraphConnection(this, layer, this.chunkSource, this.state);

    this.connections.add(connection);
    connection.registerDisposer(() => {
      this.connections.delete(connection);
    });

    return connection;
  }

  get visibleSegmentEquivalencePolicy() {
    return VisibleSegmentEquivalencePolicy.MAX_REPRESENTATIVE |
        VisibleSegmentEquivalencePolicy.NONREPRESENTATIVE_EXCLUDED;
  }

  getRoot(segment: Uint64) {
    return this.graphServer.getRoot(segment);
  }

  tabContents(
      layer: SegmentationUserLayer, context: DependentViewContext,
      tab: SegmentationGraphSourceTab) {
    const parent = document.createElement('div');
    parent.style.display = 'contents';
    const toolbox = document.createElement('div');
    toolbox.className = 'neuroglancer-segmentation-toolbox';
    toolbox.appendChild(makeToolButton(context, layer.toolBinder, {
      toolJson: GRAPHENE_MULTICUT_SEGMENTS_TOOL_ID,
      label: 'Multicut',
      title: 'Multicut segments'
    }));
    toolbox.appendChild(makeToolButton(
        context, layer.toolBinder,
        {toolJson: GRAPHENE_MERGE_SEGMENTS_TOOL_ID, label: 'Merge', title: 'Merge segments'}));
    toolbox.appendChild(makeToolButton(
        context, layer.toolBinder,
        {toolJson: GRAPHENE_FIND_PATH_TOOL_ID, label: 'Find Path', title: 'Find Path'}));
    parent.appendChild(toolbox);
    parent.appendChild(
        context
            .registerDisposer(new MulticutAnnotationLayerView(layer, layer.annotationDisplayState))
            .element);
    const tabElement = tab.element;
    tabElement.classList.add('neuroglancer-annotations-tab');
    tabElement.classList.add('neuroglancer-graphene-tab');
    return parent;
  }


  // following not used

  async merge(a: Uint64, b: Uint64): Promise<Uint64> {
    a;
    b;
    return new Uint64();
  }

  async split(include: Uint64, exclude: Uint64): Promise<{include: Uint64, exclude: Uint64}> {
    return {include, exclude};
  }

  trackSegment(id: Uint64, callback: (id: Uint64|null) => void): () => void {
    return () => {
      console.log('trackSegment... do nothing', id, callback);
    }
  }
}

class ChunkedGraphChunkSource extends SliceViewChunkSource implements
    ChunkedGraphChunkSourceInterface {
  spec: ChunkedGraphChunkSpecification;
  OPTIONS: {spec: ChunkedGraphChunkSpecification};

  constructor(chunkManager: ChunkManager, options: {
    spec: ChunkedGraphChunkSpecification,
  }) {
    super(chunkManager, options);
  }
}

class GrapheneChunkedGraphChunkSource extends
(WithParameters(WithCredentialsProvider<SpecialProtocolCredentials>()(ChunkedGraphChunkSource), ChunkedGraphSourceParameters)) {}

interface ChunkedGraphLayerDisplayState extends SegmentationDisplayState3D {}

interface TransformedChunkedGraphSource extends
    FrontendTransformedSource<SliceViewRenderLayer, ChunkedGraphChunkSource> {}

interface AttachmentState {
  chunkTransform: ValueOrError<ChunkTransformParameters>;
  displayDimensionRenderInfo: DisplayDimensionRenderInfo;
  source?: NestedStateManager<TransformedChunkedGraphSource>;
}

class SliceViewPanelChunkedGraphLayer extends SliceViewPanelRenderLayer {
  layerChunkProgressInfo = new LayerChunkProgressInfo();
  private sharedObject: SegmentationLayerSharedObject;
  readonly chunkTransform: WatchableValueInterface<ValueOrError<ChunkTransformParameters>>;

  private leafRequestsActive: SharedWatchableValue<boolean>;
  private leafRequestsStatusMessage: StatusMessage|undefined;

  constructor(
      public chunkManager: ChunkManager,
      public source: SliceViewSingleResolutionSource<ChunkedGraphChunkSource>,
      public displayState: ChunkedGraphLayerDisplayState,
      public localPosition: WatchableValueInterface<Float32Array>, nBitsForLayerId: number) {
    super();
    this.leafRequestsActive =
        this.registerDisposer(SharedWatchableValue.make(chunkManager.rpc!, true));
    this.chunkTransform = this.registerDisposer(makeCachedLazyDerivedWatchableValue(
        modelTransform =>
            makeValueOrError(() => getChunkTransformParameters(valueOrThrow(modelTransform))),
        this.displayState.transform));
    let sharedObject = this.sharedObject = this.backend = this.registerDisposer(
        new SegmentationLayerSharedObject(chunkManager, displayState, this.layerChunkProgressInfo));
    sharedObject.RPC_TYPE_ID = CHUNKED_GRAPH_LAYER_RPC_ID;
    sharedObject.initializeCounterpartWithChunkManager({
      source: source.chunkSource.addCounterpartRef(),
      localPosition: this.registerDisposer(SharedWatchableValue.makeFromExisting(
                                               chunkManager.rpc!, this.localPosition))
                         .rpcId,
      leafRequestsActive: this.leafRequestsActive.rpcId,
      nBitsForLayerId:
          this.registerDisposer(SharedWatchableValue.make(chunkManager.rpc!, nBitsForLayerId))
              .rpcId,
    });
    this.registerDisposer(sharedObject.visibility.add(this.visibility));

    this.registerDisposer(this.leafRequestsActive.changed.add(() => {
      this.showOrHideMessage(this.leafRequestsActive.value);
    }));
  }

  attach(attachment: VisibleLayerInfo<LayerView, AttachmentState>) {
    super.attach(attachment);
    const chunkTransform = this.chunkTransform.value;
    const displayDimensionRenderInfo = attachment.view.displayDimensionRenderInfo.value;
    attachment.state = {
      chunkTransform,
      displayDimensionRenderInfo,
    };
    attachment.state!.source = attachment.registerDisposer(registerNested(
        (context: RefCounted, transform: RenderLayerTransformOrError,
         displayDimensionRenderInfo: DisplayDimensionRenderInfo) => {
          const transformedSources =
              getVolumetricTransformedSources(
                  displayDimensionRenderInfo, transform, _options => [[this.source]],
                  attachment.messages, this) as TransformedChunkedGraphSource[][];
          attachment.view.flushBackendProjectionParameters();
          this.sharedObject.rpc!.invoke(CHUNKED_GRAPH_RENDER_LAYER_UPDATE_SOURCES_RPC_ID, {
            layer: this.sharedObject.rpcId,
            view: attachment.view.rpcId,
            displayDimensionRenderInfo,
            sources: serializeAllTransformedSources(transformedSources),
          });
          context;
          return transformedSources[0][0];
        },
        this.displayState.transform, attachment.view.displayDimensionRenderInfo));
  }

  isReady() {
    return true;
  }

  private showOrHideMessage(leafRequestsActive: boolean) {
    if (this.leafRequestsStatusMessage && leafRequestsActive) {
      this.leafRequestsStatusMessage.dispose();
      this.leafRequestsStatusMessage = undefined;
      StatusMessage.showTemporaryMessage('Loading chunked graph segmentation...', 3000);
    } else if ((!this.leafRequestsStatusMessage) && (!leafRequestsActive)) {
      this.leafRequestsStatusMessage = StatusMessage.showMessage(
          'At this zoom level, chunked graph segmentation will not be loaded. Please zoom in if you wish to load it.');
    }
  }
}

const GRAPHENE_MULTICUT_SEGMENTS_TOOL_ID = 'grapheneMulticutSegments';
const GRAPHENE_MERGE_SEGMENTS_TOOL_ID = 'grapheneMergeSegments';
const GRAPHENE_FIND_PATH_TOOL_ID = 'grapheneFindPath';

class MulticutAnnotationLayerView extends AnnotationLayerView {
  private _annotationStates: MergedAnnotationStates;

  constructor(public layer: SegmentationUserLayer, public displayState: AnnotationDisplayState) {
    super(layer, displayState);

    const {graphConnection: {value: graphConnection}} = layer;
    if (graphConnection instanceof GraphConnection) {
      for (const state of graphConnection.annotationLayerStates) {
        this.annotationStates.add(state);
      }
    }
  }

  get annotationStates() {
    if (this._annotationStates === undefined) {
      this._annotationStates = this.registerDisposer(new MergedAnnotationStates());
    }
    return this._annotationStates;
  }
}

const addSelection = (source: AnnotationSource|MultiscaleAnnotationSource, selection: SegmentSelection, description?: string) => {
  const annotation: Point = {
    id: '',
    point: selection.position,
    type: AnnotationType.POINT,
    properties: [],
    relatedSegments: [[selection.segmentId, selection.rootId]],
    description,
  };
  const ref = source.add(annotation);
  selection.annotationReference = ref;
}

const synchronizeAnnotationSource = (source: WatchableSet<SegmentSelection>, state: AnnotationLayerState) => {
  const annotationSource = state.source;

  annotationSource.childDeleted.add(annotationId => {
    const selection = [...source].find(selection => selection.annotationReference?.id === annotationId)
    if (selection) source.delete(selection); 
  });

  source.changed.add((x, add) => {
    if (x === null) {
      for (const annotation of annotationSource) {
        annotationSource.delete(annotationSource.getReference(annotation.id));
      }
      return;
    }

    if (add) {
      addSelection(annotationSource, x);
    } else if (x.annotationReference) {
      annotationSource.delete(x.annotationReference);
    }
  });

  // load initial state
  for (const selection of source) {
    addSelection(annotationSource, selection);
  }
}

function getMousePositionInLayerCoordinates(
    unsnappedPosition: Float32Array, layer: SegmentationUserLayer): Float32Array|
    undefined {
  const loadedSubsource = getGraphLoadedSubsource(layer)!;
  const modelTransform = loadedSubsource.getRenderLayerTransform();
  const chunkTransform = makeValueOrError(() => getChunkTransformParameters(valueOrThrow(modelTransform.value)));
  if (chunkTransform.error !== undefined) return undefined;
  const chunkPosition = new Float32Array(chunkTransform.modelTransform.unpaddedRank);
  if (!getChunkPositionFromCombinedGlobalLocalPositions(
          chunkPosition, unsnappedPosition, layer.localPosition.value,
          chunkTransform.layerRank, chunkTransform.combinedGlobalLocalToChunkTransform)) {
    return undefined;
  }
  return chunkPosition;
}

const getPoint = (layer: SegmentationUserLayer, mouseState: MouseSelectionState) => {
  if (mouseState.updateUnconditionally()) {
    return getMousePositionInLayerCoordinates(mouseState.unsnappedPosition, layer);
  }
  return undefined;
}

const MULTICUT_SEGMENTS_INPUT_EVENT_MAP = EventActionMap.fromObject({
  'at:shift?+control+mousedown0': {action: 'set-anchor'},
  'at:touchtap1x1': {action: 'nge-tap-place'},
  'at:touchtap1x2': {action: 'nge-tap-double'},
  'at:shift?+keyg': {action: 'swap-group'},
  'at:shift?+enter': {action: 'submit'},
});

/** nge: "Keep open after a cut", the preference at the right end of the cut
 *  bar (Nseraf 2026-10-12: merge stayed open after a merge, cut closed after a
 *  cut). Off by default, so the cut tool closes after a successful cut, as it
 *  always has. On, it stays for the next cut. Kept in this browser; the bar
 *  (src/components/SplitMergeOverlay.vue) writes it, the tool reads it at
 *  each submit. */
export const NGE_KEEP_CUT_OPEN_KEY = 'nge-keep-cut-open';
export function ngeKeepCutOpen(): boolean {
  try { return localStorage.getItem(NGE_KEEP_CUT_OPEN_KEY) === '1'; } catch { return false; }
}

/** nge: tap to place (Nseraf, report 135, on a Galaxy Tab with no keyboard).
 *  In Cut, Merge and Find Path a one finger tap does what Ctrl+click does.
 *  A double tap still shows or hides a segment, as everywhere else: the
 *  point waits a moment, and a second tap inside that moment takes it back
 *  and passes the double tap on. A second tap after the point is placed is
 *  a new tap. Each tool's map binds 'at:touchtap1x1' to 'nge-tap-place' and
 *  'at:touchtap1x2' to 'nge-tap-double'. */
const NGE_TAP_WAIT_MS = 300;
function ngeBindTapToPlace(activation: ToolActivation<any>, place: () => void) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedule = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => { timer = undefined; place(); }, NGE_TAP_WAIT_MS);
  };
  activation.registerDisposer(() => { if (timer !== undefined) clearTimeout(timer); });
  activation.bindAction('nge-tap-place', event => {
    event.stopPropagation();
    schedule();
  });
  activation.bindAction('nge-tap-double', event => {
    event.stopPropagation();
    if (timer === undefined) { schedule(); return; }
    clearTimeout(timer);
    timer = undefined;
    event.target?.dispatchEvent(new CustomEvent('action:select', {bubbles: true, detail: event.detail}));
  });
}

class MulticutSegmentsTool extends LayerTool<SegmentationUserLayer> {
  toJSON() {
    return GRAPHENE_MULTICUT_SEGMENTS_TOOL_ID;
  }

  activate(activation: ToolActivation<this>) {
    const {layer} = this;
    const {graphConnection: {value: graphConnection}} = layer;
    if (!graphConnection || !(graphConnection instanceof GraphConnection)) return;
    const {state: {multicutState}, segmentsState} = graphConnection;
    if (multicutState === undefined) return;

    const {body, header} = makeToolActivationStatusMessageWithHeader(activation);
    header.textContent = 'Multicut segments';
    body.classList.add('graphene-tool-status', 'graphene-multicut');
    body.appendChild(makeIcon({
      text: 'Swap',
      title: 'Swap group',
      onClick: () => {
        multicutState.swapGroup();
      }
    }));
    body.appendChild(makeIcon({
      text: 'Clear',
      title: 'Clear multicut',
      onClick: () => {
        multicutState.reset();
      }
    }));
    const submitAction = async () => {
      submitIcon.classList.toggle('disabled', true);
      const loadedSubsource = getGraphLoadedSubsource(this.layer)!;
      const annotationToNanometers =
          loadedSubsource.loadedDataSource.transform.inputSpace.value.scales.map(x => x / 1e-9);
      graphConnection.submitMulticut(annotationToNanometers).then(success => {
        submitIcon.classList.toggle('disabled', false);
        // nge: the cut bar shows how it went (in place, when the tool stays).
        window.dispatchEvent(new CustomEvent('nge-cut-submitted', {detail: {success}}));
        // With "Keep open after a cut" on, the tool stays for the next cut,
        // as merge does. Off, it closes after a cut, as it always has.
        if (success && !ngeKeepCutOpen()) {
          activation.cancel();
        }
      }, () => {
        // The server refused or failed: the status bar has said why. The
        // Submit icon comes back so the next try can go.
        submitIcon.classList.toggle('disabled', false);
        window.dispatchEvent(new CustomEvent('nge-cut-submitted', {detail: {success: false}}));
      });
    }
    const submitIcon = makeIcon({
      text: 'Submit',
      title: 'Submit multicut',
      onClick: () => {
        submitAction();
      }
    });
    body.appendChild(submitIcon);
    // nge (Nseraf 2026-10-11; for everyone since 2026-10-13): colour the cell in 3D by the side each
    // part would fall on, before anything is cut. The app does the work
    // (src/util/cut_preview.ts); the colours go when the points change, when
    // the cut is sent, and when the tool closes.
    const previewIcon = makeIcon({
      text: 'Preview',
      title: 'Show in 3D, in red and blue, which side of the cut each part of the cell would fall on. Nothing is cut.',
      onClick: () => {
        window.dispatchEvent(new CustomEvent('nge-cut-preview', {detail: {layer: this.layer}}));
      }
    });
    previewIcon.classList.add('nge-cut-preview-button');
    body.appendChild(previewIcon);
    const clearPreview = () => window.dispatchEvent(new CustomEvent('nge-cut-preview-clear'));
    activation.registerDisposer(multicutState.changed.add(clearPreview));
    activation.registerDisposer(clearPreview);
    const activeGroupIndicator = document.createElement('div');
    activeGroupIndicator.className = 'activeGroupIndicator';
    activeGroupIndicator.innerHTML = 'Active Group: ';
    body.appendChild(activeGroupIndicator);

    const {displayState} = this.layer;
    // Ensure we use the same segmentationGroupState while activated.
    const segmentationGroupState = displayState.segmentationGroupState.value;
    const priorBaseSegmentHighlighting = displayState.baseSegmentHighlighting.value;
    const priorHighlightColor = displayState.highlightColor.value;


    multicutState.toolActive = true;
    activation.registerDisposer(() => { multicutState.toolActive = false; });
    activation.bindInputEventMap(MULTICUT_SEGMENTS_INPUT_EVENT_MAP);
    activation.registerDisposer(() => {
      resetMulticutDisplay();
      displayState.baseSegmentHighlighting.value = priorBaseSegmentHighlighting;
      displayState.highlightColor.value = priorHighlightColor;
    });

    const resetMulticutDisplay = () => {
      resetTemporaryVisibleSegmentsState(segmentationGroupState);
      displayState.useTempSegmentStatedColors2d.value = false;
      displayState.tempSegmentStatedColors2d.value
          .clear();  // TODO, should only clear those that are in temp sets
      displayState.tempSegmentDefaultColor2d.value = undefined;
      displayState.highlightColor.value = undefined;
    };

    const updateMulticutDisplay = () => {
      resetMulticutDisplay();
      activeGroupIndicator.classList.toggle('blueGroup', multicutState.blueGroup.value);

      const focusSegment = multicutState.focusSegment.value;
      if (focusSegment === undefined) return;

      displayState.baseSegmentHighlighting.value = true;
      displayState.highlightColor.value =
          multicutState.blueGroup.value ? BLUE_COLOR_HIGHTLIGHT : RED_COLOR_HIGHLIGHT;
      segmentsState.useTemporaryVisibleSegments.value = true;
      segmentsState.useTemporarySegmentEquivalences.value = true;

      // add to focus segments and temporary sets
      segmentsState.temporaryVisibleSegments.add(focusSegment);

      for (const segment of multicutState.segments) {
        segmentsState.temporaryVisibleSegments.add(segment);
      }

      // all other segments are added to the focus segment equivalences
      for (const equivalence of segmentsState.segmentEquivalences.setElements(focusSegment)) {
        if (!segmentsState.temporaryVisibleSegments.has(equivalence)) {
          segmentsState.temporarySegmentEquivalences.link(focusSegment, equivalence);
        }
      }

      // set colors
      displayState.tempSegmentDefaultColor2d.value = MULTICUT_OFF_COLOR;
      displayState.tempSegmentStatedColors2d.value.set(focusSegment, TRANSPARENT_COLOR_PACKED);

      for (const segment of multicutState.redSegments) {
        displayState.tempSegmentStatedColors2d.value.set(segment, RED_COLOR_SEGMENT_PACKED);
      }
      for (const segment of multicutState.blueSegments) {
        displayState.tempSegmentStatedColors2d.value.set(segment, BLUE_COLOR_SEGMENT_PACKED);
      }

      displayState.useTempSegmentStatedColors2d.value = true;
    };

    updateMulticutDisplay();

    activation.registerDisposer(multicutState.changed.add(updateMulticutDisplay));

    activation.bindAction('swap-group', event => {
      event.stopPropagation();
      multicutState.swapGroup();
    });

    const setAnchor = () => {
      const currentSegmentSelection =
          maybeGetSelection(this, segmentationGroupState.visibleSegments);
      if (!currentSegmentSelection) return;
      const {rootId, segmentId} = currentSegmentSelection;
      const {focusSegment, segments} = multicutState;
      if (focusSegment.value === undefined) {
        focusSegment.value = rootId.clone();
      }
      if (!Uint64.equal(focusSegment.value, rootId)) {
        StatusMessage.showTemporaryMessage(
            `The selected supervoxel has root segment ${
                rootId.toString()}, but the supervoxels already selected have root ${
                focusSegment.value.toString()}`,
            12000);
        return;
      }
      const isRoot = Uint64.equal(rootId, segmentId);
      if (!isRoot) {
        for (const segment of segments) {
          if (Uint64.equal(segment, segmentId)) {
            StatusMessage.showTemporaryMessage(
                `Supervoxel ${segmentId.toString()} has already been selected`, 7000);
            return;
          }
        }
      }
      multicutState.activeGroup.add(currentSegmentSelection);
    };
    activation.bindAction('set-anchor', event => {
      event.stopPropagation();
      setAnchor();
    });
    ngeBindTapToPlace(activation, setAnchor);

    activation.bindAction('submit', event => {
      event.stopPropagation();
      submitAction();
    });
  }

  get description() {
    return `multicut`;
  }
}

const maybeGetSelection =
    (tool: LayerTool<SegmentationUserLayer>, visibleSegments: Uint64Set): SegmentSelection|
    undefined => {
      const {layer, mouseState} = tool;
      const {segmentSelectionState: {value, baseValue}} = layer.displayState;
      if (!baseValue || !value) return;
      if (!visibleSegments.has(value)) {
        StatusMessage.showTemporaryMessage(
            'The selected supervoxel is of an unselected segment', 7000);
        return;
      }
      const point = getPoint(layer, mouseState);
      if (point === undefined) return;
      return {
        rootId: value.clone(),
        segmentId: baseValue.clone(),
        position: point,
      };
    };

const wait = (t: number) => {
  return new Promise((f, _r) => {
    setTimeout(f, t);
  });
}

/** How long one merge may wait on the server before it gives up. */
const MERGE_WAIT_MS = 90_000;

interface MergeSubmission {
  id: string;
  locked: boolean;
  /** Set while the merge is being sent: stops it, with the reason to show. */
  cancel?: (why: string) => void;
  /** The player cleared it from the queue while it was being sent. */
  cleared?: boolean;
  error?: string;
  status?: string;
  sink: SegmentSelection;
  source?: SegmentSelection;
  mergedRoot?: Uint64;
}

export class MergeSegmentsPlaceLineTool extends PlaceLineTool {
  getBaseSegment = true;
  constructor(layer: SegmentationUserLayer, private annotationState: AnnotationLayerState) {
    super(layer, {});
    const {inProgressAnnotation} = this;
    const {displayState} = annotationState;
    if (!displayState) return; // TODO, this happens when reloading the page when a toggle tool is up
    const {disablePicking} = displayState;
    this.registerDisposer(inProgressAnnotation.changed.add(() => {
      disablePicking.value = inProgressAnnotation.value !== undefined;
    }));
  }
  get annotationLayer() {
    return this.annotationState;
  }
  get description() {
    return `merge line`;
  }
  toJSON() {
    return ANNOTATE_MERGE_LINE_TOOL_ID;
  }
}

function lineToSubmission(line: Line, pending: boolean): MergeSubmission {
  const relatedSegments = line.relatedSegments![0];
  const res: MergeSubmission = {
    id: line.id,
    locked: false,
    sink: {
      position: line.pointA.slice(),
      rootId: relatedSegments[0].clone(),
      segmentId: relatedSegments[1].clone(),
    }
  };
  if (!pending) {
    res.source = {
      position: line.pointB.slice(),
      rootId: relatedSegments[2].clone(),
      segmentId:relatedSegments[3].clone(),
    };
  }
  return res;
}

function mergeToLine(submission: MergeSubmission): Line {
  const {sink, source} = submission;
  const res: Line = {
    id: submission.id,
    type: AnnotationType.LINE,
    pointA: sink.position.slice(),
    pointB: source!.position.slice(),
    relatedSegments: [[sink.rootId.clone(), sink.segmentId.clone(), source!.rootId.clone(), source!.segmentId.clone()]],
    properties: [],
  };
  return res;
}

// nge: 20, as in stock neuroglancer (was 10 here; player request 2026-10-08).
const MAX_MERGE_COUNT = 20;

// on error, copy (also clean up error message)

const MERGE_SEGMENTS_INPUT_EVENT_MAP = EventActionMap.fromObject({
  'at:shift?+enter': {action: 'submit'},
  // EyeWire II: merge places its own points. Without this, ctrl+click ran the
  // viewer's general "annotate" action, which uses whichever layer is
  // selected: entering merge straight from an annotation layer dropped an
  // annotation point instead of picking a segment (andrearwen 2026-10-06).
  // The cut tool has always bound its own click the same way.
  // Since 2026-10-08 (Nik): opening merge selects the segmentation layer, so
  // that case is covered at the door, and a player who then SWITCHES to an
  // annotation layer on purpose gets an annotation from ctrl+click, without
  // having to close merge first. See 'place-merge-point' below.
  'at:shift?+control+mousedown0': {action: 'place-merge-point'},
  'at:touchtap1x1': {action: 'nge-tap-place'},
  'at:touchtap1x2': {action: 'nge-tap-double'},
});

class MergeSegmentsTool extends LayerTool<SegmentationUserLayer> {
  activate(activation: ToolActivation<this>) {
    const {graphConnection: {value: graphConnection}, tool} = this.layer;
    if (!graphConnection || !(graphConnection instanceof GraphConnection)) return;
    const {state: {mergeState}} = graphConnection;
    if (mergeState === undefined) return;
    const {merges, autoSubmit} = mergeState;

    const lineTool = new MergeSegmentsPlaceLineTool(this.layer, graphConnection.mergeAnnotationState);
    tool.value = lineTool;
    activation.registerDisposer(() => {
      tool.value = undefined;
    });
    const {body, header} = makeToolActivationStatusMessageWithHeader(activation);
    header.textContent = 'Merge segments';
    body.classList.add('graphene-tool-status', 'graphene-merge-segments');
    activation.bindInputEventMap(MERGE_SEGMENTS_INPUT_EVENT_MAP);
    // Merge begins on the segmentation layer, whichever layer it was opened
    // from (so a ctrl+click right after opening always picks a segment).
    // When merge closes, the player goes back to the layer they opened it
    // from. They used to stay there throughout; since this switch was
    // added they were left on the segmentation layer after every merge
    // (Andrearwen 2026-10-08: "stopped returning to the annotation tab the
    // user was on after they do a merge"). Not if they picked another layer
    // themselves in the meantime, or the one they came from is gone.
    try {
      const sel = (window as any).viewer?.selectedLayer;
      const cameFrom = sel?.layer;
      if (sel && cameFrom !== this.layer.managedLayer) {
        sel.layer = this.layer.managedLayer;
        activation.registerDisposer(() => {
          try {
            const still = (window as any).viewer?.layerManager?.managedLayers?.includes(cameFrom);
            if (cameFrom && still && sel.layer === this.layer.managedLayer) sel.layer = cameFrom;
          } catch { /* nothing to go back to */ }
        });
      }
    } catch { /* the viewer is not up yet */ }
    const placeMergePoint = () => {
      // The player has moved to an annotation layer with a tool picked (to
      // mark a true end, say): the click is an annotation there. Back on the
      // segmentation layer it is a merge point again. Merge stays open.
      try {
        const chosen = (window as any).viewer?.selectedLayer?.layer?.layer;
        const annotate = chosen && chosen !== this.layer && (chosen.constructor as any)?.type === 'annotation' ? chosen.tool?.value : undefined;
        if (annotate && typeof annotate.trigger === 'function') { annotate.trigger(this.mouseState); return; }
      } catch { /* fall through to the merge point */ }
      lineTool.trigger(this.mouseState);
    };
    activation.bindAction('place-merge-point', event => {
      event.stopPropagation();
      placeMergePoint();
    });
    ngeBindTapToPlace(activation, placeMergePoint);
    const submitAction = async () => {
      if (merges.value.filter(x => x.locked).length) return;
      submitIcon.classList.toggle('disabled', true);
      await graphConnection.bulkMerge(merges.value);
      submitIcon.classList.toggle('disabled', false);
    }
    const submitIcon = makeIcon({
      text: 'Submit',
      title: 'Submit merge',
      onClick: async () => {
        submitAction();
      }});
    body.appendChild(submitIcon);
    activation.bindAction('submit', async event => {
      event.stopPropagation();
      submitAction();
    });
    body.appendChild(makeIcon({
      text: 'Clear',
      title: 'Clear pending merges',
      onClick: () => {
        lineTool.deactivate();
        for (const merge of [...merges.value]) {
          // One still being sent is stopped first; it then leaves the queue
          // with the rest instead of sitting there locked.
          if (merge.locked) merge.cleared = true;
          if (merge.locked) merge.cancel?.('Stopped. If the server had already taken this merge it may still go through: check the cell.');
          graphConnection.deleteMergeSubmission(merge);
        }
      }
    }));
    const checkbox = activation.registerDisposer(new TrackableBooleanCheckbox(autoSubmit));
    const label = document.createElement('label');
    label.appendChild(document.createTextNode('auto-submit'));
    label.title = 'auto-submit merges';
    label.appendChild(checkbox.element);
    body.appendChild(label);
    const points = document.createElement('div');
    points.classList.add('graphene-merge-segments-merges');
    body.appendChild(points);

    const segmentWidgetFactory = SegmentWidgetFactory.make(this.layer.displayState, /*includeUnmapped=*/ true);
    const makeWidget = (id: Uint64MapEntry) => {
      const row = segmentWidgetFactory.getWithNormalizedId(id);
      row.classList.add('neuroglancer-segment-list-entry-double-line');
      return row;
    };

    const createPointElement = (id: Uint64) => {
      const containerEl =  document.createElement('div');
      containerEl.classList.add('graphene-merge-segments-point')
      const widget = makeWidget(augmentSegmentId(this.layer.displayState, id));
      containerEl.appendChild(widget);
      return containerEl;
    };

    const createSubmissionElement = (submission: MergeSubmission) => {
      const containerEl =  document.createElement('div');
      containerEl.classList.add('graphene-merge-segments-submission');
      containerEl.appendChild(createPointElement(submission.sink.rootId));
      if (submission.source) {
        containerEl.appendChild(document.createElement('div')).textContent = "ꕹ";
        containerEl.appendChild(createPointElement(submission.source.rootId));
      }
      if (!submission.locked) {
        containerEl.appendChild(makeDeleteButton({
          title: 'Delete merge',
          onClick: event => {
            event.stopPropagation();
            event.preventDefault();
            graphConnection.deleteMergeSubmission(submission);
          },
        }));
      }
      if (submission.status) {
        const statusEl =  document.createElement('div');
        statusEl.classList.add('graphene-merge-segments-submission-status');
        statusEl.textContent = submission.status;
        containerEl.appendChild(statusEl);
      }
      return containerEl;
    };

    const updateUI = () => {
      while (points.firstChild) {
        points.removeChild(points.firstChild);
      }
      for (let submission of merges.value) {
        points.appendChild(createSubmissionElement(submission));
      }
    };
    activation.registerDisposer(merges.changed.add(updateUI));
    updateUI();
  }

  toJSON() {
    return GRAPHENE_MERGE_SEGMENTS_TOOL_ID;
  }

  get description() {
    return `merge segments`;
  }
}

const FIND_PATH_INPUT_EVENT_MAP = EventActionMap.fromObject({
  'at:shift?+enter': {action: 'submit'},
  'at:shift?+control+mousedown0': {action: 'add-point'},
  'at:touchtap1x1': {action: 'nge-tap-place'},
  'at:touchtap1x2': {action: 'nge-tap-double'},
});

class FindPathTool extends LayerTool<SegmentationUserLayer> {
  activate(activation: ToolActivation<this>) {
    const {layer} = this;
    const {graphConnection: {value: graphConnection}} = layer;
    if (!graphConnection || !(graphConnection instanceof GraphConnection)) return;
    const {state: {findPathState}, findPathAnnotationState} = graphConnection;
    const {source, target, precisionMode} = findPathState;

    // Ensure we use the same segmentationGroupState while activated.
    const segmentationGroupState = this.layer.displayState.segmentationGroupState.value;

    const {body, header} = makeToolActivationStatusMessageWithHeader(activation);
    header.textContent = 'Find Path';
    // nge: closing the tool stops a search still running.
    activation.registerDisposer(ngeStopFindPath);
    body.classList.add('graphene-tool-status', 'graphene-find-path');

    const submitAction = () => {
      findPathState.triggerPathUpdate.dispatch();
    }

    body.appendChild(makeIcon({
      text: 'Submit',
      title: 'Submit Find Path',
      onClick: () => {
        submitAction();
      }}));
    
    body.appendChild(makeIcon({
      text: 'Clear',
      title: 'Clear Find Path',
      onClick: () => {
        findPathState.source.reset();
        findPathState.target.reset();
        findPathState.centroids.reset();
      }}));

    const checkbox = activation.registerDisposer(new TrackableBooleanCheckbox(precisionMode));

    const label = document.createElement('label');
    const labelText = document.createElement('span');
    labelText.textContent = "Precision mode: ";
    label.appendChild(labelText);
    label.title = 'Precision mode returns a more accurate path, but takes longer.';
    label.appendChild(checkbox.element);
    body.appendChild(label);

    const annotationElements = document.createElement('div');
    annotationElements.classList.add('find-path-annotations');
    body.appendChild(annotationElements);

    const bindings = getDefaultAnnotationListBindings();
    this.registerDisposer(new MouseEventBinder(annotationElements, bindings));

    const updateAnnotationElements = () => {
      removeChildren(annotationElements);
      const maxColumnWidths = [0, 0, 0];
      const globalDimensionIndices = [0, 1, 2];
      const localDimensionIndices: number[] = [];
      const template = '[symbol] 2ch [dim] var(--neuroglancer-column-0-width) [dim] var(--neuroglancer-column-1-width) [dim] var(--neuroglancer-column-2-width) [delete] min-content';
      
      const endpoints = [source, target];
      const endpointAnnotations = endpoints.map(x => x.value?.annotationReference?.value)
        .filter(x => x) as Annotation[];
      for (const annotation of endpointAnnotations) {
        const [element, elementColumnWidths] = makeAnnotationListElement(this.layer, annotation, findPathAnnotationState, template, globalDimensionIndices, localDimensionIndices);
        for (const [column, width] of elementColumnWidths.entries()) {
          maxColumnWidths[column] = width;
        }
        annotationElements.appendChild(element);
      }
      for (const [column, width] of maxColumnWidths.entries()) {
        annotationElements.style.setProperty(`--neuroglancer-column-${column}-width`, `${width+2}ch`);
      }
    };

    findPathState.changed.add(updateAnnotationElements);
    updateAnnotationElements();

    activation.bindInputEventMap(FIND_PATH_INPUT_EVENT_MAP);

    activation.bindAction('submit', event => {
      event.stopPropagation();
      submitAction();
    });

    const addPoint = () => {
      (async () => {
        if (!source.value) { // first selection
          const selection = maybeGetSelection(this, segmentationGroupState.visibleSegments);
          if (selection) {
            source.value = selection;
          }
        } else if (!target.value) {
          const selection = maybeGetSelection(this, segmentationGroupState.visibleSegments);
          if (selection) {
            target.value = selection;
          }
        }
      })()
    };
    activation.bindAction('add-point', event => {
      console.log('add-point');
      event.stopPropagation();
      addPoint();
    });
    ngeBindTapToPlace(activation, addPoint);
  }

  toJSON() {
    return GRAPHENE_FIND_PATH_TOOL_ID;
  }

  get description() {
    return `find path`;
  }
}

registerTool(SegmentationUserLayer, GRAPHENE_MULTICUT_SEGMENTS_TOOL_ID, layer => {
  return new MulticutSegmentsTool(layer, true);
});

registerTool(SegmentationUserLayer, GRAPHENE_MERGE_SEGMENTS_TOOL_ID, layer => {
  return new MergeSegmentsTool(layer, true);
});

registerTool(SegmentationUserLayer, GRAPHENE_FIND_PATH_TOOL_ID, layer => {
  return new FindPathTool(layer, true);
});

const ANNOTATE_MERGE_LINE_TOOL_ID = 'annotateMergeLine';

registerLegacyTool(
    ANNOTATE_MERGE_LINE_TOOL_ID,
    (layer, options) => new MergeSegmentsPlaceLineTool(<SegmentationUserLayer>layer, options));

// ── EyeWire II Highlight mode (src/util/highlight.ts) ─────────────────────
// The same two things Find Path does, without its single source/target state:
// what is under the cursor, and the path through a cell between two picks.

/** A position in a graphene layer's own coordinates (as a SegmentSelection,
 *  the cut and merge points, keeps it) in the viewer's global coordinates,
 *  matched axis by axis by name. Undefined until the layer has loaded. Where
 *  the view's voxel size differs from the data's, the raw position lands far
 *  from the cell (Celia D, report 258: "it jumps you to outerspace"). */
export function ngeGrapheneLayerToGlobal(
    layer: SegmentationUserLayer, position: ArrayLike<number>,
    global: {rank: number, names: readonly string[], scales: Float64Array}): Float32Array|undefined {
  const sub = getGraphLoadedSubsource(layer);
  if (sub === undefined) return undefined;
  const input = sub.loadedDataSource.transform.inputSpace.value;
  const out = new Float32Array(global.rank);
  for (let g = 0; g < global.rank; ++g) {
    const i = input.names.indexOf(global.names[g]);
    if (i < 0 || i >= position.length || !global.scales[g]) return undefined;
    out[g] = position[i] * input.scales[i] / global.scales[g];
  }
  return out;
}

/** The cell under the cursor on a graphene layer: its root and the position
 *  in the layer's own coordinates. Undefined when the cursor is not on a
 *  visible cell. */
export function ngeGrapheneSelectionUnderMouse(
    layer: SegmentationUserLayer, mouseState: MouseSelectionState): SegmentSelection|undefined {
  const {segmentSelectionState: {value, baseValue}} = layer.displayState;
  if (!value) return undefined;
  if (!layer.displayState.segmentationGroupState.value.visibleSegments.has(value)) return undefined;
  const point = getPoint(layer, mouseState);
  if (point === undefined) return undefined;
  return {rootId: value.clone(), segmentId: (baseValue ?? value).clone(), position: point};
}

/** The path through the cell between two picks, as points in NANOMETERS
 *  (one per level 2 chunk along the way). */
export async function ngeGrapheneFindPath(
    layer: SegmentationUserLayer, a: SegmentSelection, b: SegmentSelection,
    precise = true): Promise<number[][]> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  const loadedSubsource = getGraphLoadedSubsource(layer)!;
  const toNm = loadedSubsource.loadedDataSource.transform.inputSpace.value.scales.map(x => x / 1e-9);
  // Precise: points at the real middle of each piece of the branch. The
  // quick mode returns chunk positions, a staircase beside the branch.
  const centroids = await connection.graph.graphServer.findPath(a, b, precise, toNm);
  return centroids.map(pt => pt.map((v, i) => v * toNm[i]));
}

/** The branch graph of a cell for Highlight mode: the links between its
 *  pieces and a point (in NANOMETERS) inside each piece. */
export async function ngeGrapheneBranchGraph(layer: SegmentationUserLayer, rootId: string):
    Promise<{edges: [string, string][], points: Map<string, number[]>}> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  const server = connection.graph.graphServer as any;
  const edges: [string, string][] = await server.ngeBranchEdges(rootId);
  const ids = new Set<string>();
  for (const [a, b] of edges) { ids.add(a); ids.add(b); }
  const points: Map<string, number[]> = await server.ngePiecePoints([...ids]);
  return {edges, points};
}

/** For the cut preview: the links between a cell's pieces, and the block of
 *  the volume each piece lies in (nanometres, [x0, y0, z0, x1, y1, z1]). */
export async function ngeGrapheneCutGraph(layer: SegmentationUserLayer, rootId: string):
    Promise<{edges: [string, string][], boxes: Map<string, number[]>}> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  const server = connection.graph.graphServer as any;
  const edges: [string, string][] = await server.ngeBranchEdges(rootId);
  const ids = new Set<string>();
  for (const [a, b] of edges) { ids.add(a); ids.add(b); }
  const boxes: Map<string, number[]> = await server.ngeChunkBoxes([...ids]);
  return {edges, boxes};
}

/** For the cut preview (src/util/cut_preview.ts): the red and blue points
 *  placed so far, and the groups of supervoxels the server says a cut between
 *  them would leave. Which group is which side is decided by the app, from
 *  the points each group holds, not from the order they come in. Nothing is
 *  cut. */
export async function ngeGrapheneCutPreview(layer: SegmentationUserLayer): Promise<{
  rootId: string, redPicks: string[], bluePicks: string[], groups: string[][], illegal: boolean,
}> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  const {multicutState} = connection.state;
  const red = [...multicutState.sinks], blue = [...multicutState.sources];
  if (!red.length || !blue.length) throw new Error('Place at least one red and one blue point first.');
  const loadedSubsource = getGraphLoadedSubsource(layer)!;
  const toNm = loadedSubsource.loadedDataSource.transform.inputSpace.value.scales.map(x => x / 1e-9);
  const server = connection.graph.graphServer as any;
  const preview: {sides: string[][], illegal: boolean} = await server.ngeSplitPreview(red, blue, toNm);
  return {
    rootId: red[0].rootId.toString(),
    redPicks: red.map(x => x.segmentId.toString()), bluePicks: blue.map(x => x.segmentId.toString()),
    groups: preview.sides, illegal: preview.illegal,
  };
}

/** What ngeGrapheneCutVoxels found: the voxels near the points, each marked
 *  with the side its supervoxel is on, or why they could not be read. */
export interface NgeCutVoxels {
  ok: boolean;
  /** Why not, in plain words, when ok is false. */
  why: string;
  /** How the volume is stored, for the console line that says what happened. */
  stored: {host: string, scales: {key: string, nm: number[], encoding: string, sharded: boolean}[], used: string};
  /** Corner of the region in nanometres, the size of one voxel, and how many. */
  originNm: number[]; voxelNm: number[]; dims: number[];
  /** x fastest, then y, then z. 0 neither side, 1 red, 2 blue. */
  labels: Uint8Array;
  files: {asked: number, read: number, empty: number, failed: number};
  marked: {red: number, blue: number};
}

/**
 * For the cut preview: the supervoxel under every voxel near the points, as
 * the side the server's preview put that supervoxel on.
 *
 * The cell's surface in 3D is not made of supervoxels, so the only way to
 * say where one is, is the volume itself: the same files the 2D view loads.
 * They are read here at a coarse scale (about 30 nm a voxel or more), only
 * around the points, where the server's answer is.
 */
export async function ngeGrapheneCutVoxels(
    layer: SegmentationUserLayer, redSupervoxels: readonly string[], blueSupervoxels: readonly string[]): Promise<NgeCutVoxels> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  const info = (connection.graph as any).info as GrapheneMultiscaleVolumeInfo;
  // Where the points are, in nanometres, and the room round them the server looks at.
  const {multicutState} = connection.state;
  const picks = [...multicutState.sinks, ...multicutState.sources];
  const loadedSubsource = getGraphLoadedSubsource(layer)!;
  const toNm = loadedSubsource.loadedDataSource.transform.inputSpace.value.scales.map(x => x / 1e-9);
  const PAD = [2600, 2600, 1500];
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (const pick of picks) for (let i = 0; i < 3; i++) {
    const v = pick.position[i] * toNm[i];
    lo[i] = Math.min(lo[i], v - PAD[i]); hi[i] = Math.max(hi[i], v + PAD[i]);
  }
  return ngeReadSideVoxels(info.dataUrl, info.scales, lo, hi, redSupervoxels, blueSupervoxels);
}

/** The reading itself, for any volume stored the plain way: every voxel of
 *  the region lo..hi (nanometres) marked 1 or 2 if its id is in the first or
 *  the second list. Apart from the graph, so it can be tried on a public
 *  volume (the cut preview's own test). */
export async function ngeReadSideVoxels(
    dataUrl: string, scales: readonly any[], lo: readonly number[], hi: readonly number[],
    redSupervoxels: readonly string[], blueSupervoxels: readonly string[]): Promise<NgeCutVoxels> {
  const none = (why: string, stored: NgeCutVoxels['stored']): NgeCutVoxels => ({
    ok: false, why, stored, originNm: [0, 0, 0], voxelNm: [1, 1, 1], dims: [0, 0, 0], labels: new Uint8Array(0),
    files: {asked: 0, read: 0, empty: 0, failed: 0}, marked: {red: 0, blue: 0},
  });
  const info = {dataUrl, scales};
  const stored: NgeCutVoxels['stored'] = {
    host: String(info.dataUrl).replace(/^(\w+:\/\/[^/]+\/[^/]*).*$/, '$1'),
    scales: info.scales.map(sc => ({
      key: sc.key, nm: Array.from(sc.resolution).slice(0, 3), encoding: VolumeChunkEncoding[sc.encoding], sharded: sc.sharding !== undefined,
    })),
    used: '',
  };
  if (!lo.every(Number.isFinite) || !hi.every(Number.isFinite)) return none('There are no points.', stored);
  // The finest scale that is not finer than about 30 nm a voxel, then coarser
  // ones if the region would still be too many voxels.
  const MAX_VOXELS = 9e6;
  const usable = (sc: any) => sc.sharding === undefined &&
      (sc.encoding === VolumeChunkEncoding.COMPRESSED_SEGMENTATION || sc.encoding === VolumeChunkEncoding.RAW);
  let scale: any = undefined, vlo: number[] = [], vhi: number[] = [];
  for (const sc of info.scales) {
    if (sc.resolution[0] < 30 && sc !== info.scales[info.scales.length - 1]) continue;
    const a = [0, 1, 2].map(i => Math.max(sc.voxelOffset[i], Math.floor(lo[i] / sc.resolution[i])));
    const b = [0, 1, 2].map(i => Math.min(sc.voxelOffset[i] + sc.size[i], Math.ceil(hi[i] / sc.resolution[i])));
    if (b.some((v, i) => v <= a[i])) continue;
    if ((b[0] - a[0]) * (b[1] - a[1]) * (b[2] - a[2]) > MAX_VOXELS) continue;
    scale = sc; vlo = a; vhi = b;
    break;
  }
  if (scale === undefined) return none('No scale of the volume fits the region round the points.', stored);
  stored.used = scale.key;
  if (!usable(scale)) {
    return none(`The volume is stored in a way this preview cannot read yet (${VolumeChunkEncoding[scale.encoding]}${scale.sharding !== undefined ? ', sharded' : ''}).`, stored);
  }
  // Each side's supervoxels, as the two halves of their 64 bit ids.
  const sideOf = new Map<number, Map<number, number>>();
  const add = (ids: readonly string[], side: number) => {
    for (const id of ids) {
      let n: bigint;
      try { n = BigInt(id); } catch { continue; }
      const high = Number(n >> BigInt(32)), low = Number(n & BigInt(0xffffffff));
      (sideOf.get(high) ?? sideOf.set(high, new Map()).get(high)!).set(low, side);
    }
  };
  add(redSupervoxels, 1); add(blueSupervoxels, 2);

  const dims = [vhi[0] - vlo[0], vhi[1] - vlo[1], vhi[2] - vlo[2]];
  const labels = new Uint8Array(dims[0] * dims[1] * dims[2]);
  const files = {asked: 0, read: 0, empty: 0, failed: 0};
  const marked = {red: 0, blue: 0};
  const cs: ArrayLike<number> = scale.chunkSizes[0];
  const off: ArrayLike<number> = scale.voxelOffset, size: ArrayLike<number> = scale.size;
  const g0 = [0, 1, 2].map(i => Math.floor((vlo[i] - off[i]) / cs[i]));
  const g1 = [0, 1, 2].map(i => Math.floor((vhi[i] - 1 - off[i]) / cs[i]));
  const todo: number[][] = [];
  for (let gz = g0[2]; gz <= g1[2]; gz++) for (let gy = g0[1]; gy <= g1[1]; gy++) for (let gx = g0[0]; gx <= g1[0]; gx++) todo.push([gx, gy, gz]);
  if (todo.length > 400) return none('The region round the points is spread over too many files.', stored);
  const value = new Uint64();
  const readOne = async (g: number[]) => {
    const at = [0, 1, 2].map(i => off[i] + g[i] * cs[i]);
    const n = [0, 1, 2].map(i => Math.min(cs[i], off[i] + size[i] - at[i]));
    const url = `${info.dataUrl}/${scale.key}/${at[0]}-${at[0] + n[0]}_${at[1]}-${at[1] + n[1]}_${at[2]}-${at[2] + n[2]}`;
    files.asked++;
    let buffer: ArrayBuffer;
    try {
      buffer = await cancellableFetchSpecialOk(undefined, url, {}, responseArrayBuffer);
    } catch (e) {
      if (isNotFoundError(e)) files.empty++; else files.failed++;
      return;
    }
    files.read++;
    const compressed = scale.encoding === VolumeChunkEncoding.COMPRESSED_SEGMENTATION;
    const data = new Uint32Array(buffer, 0, Math.floor(buffer.byteLength / 4));
    const block: ArrayLike<number> = scale.compressedSegmentationBlockSize ?? [8, 8, 8];
    const base = compressed ? data[0] : 0;
    // Only the part of this file inside the region.
    const x0 = Math.max(at[0], vlo[0]), x1 = Math.min(at[0] + n[0], vhi[0]);
    const y0 = Math.max(at[1], vlo[1]), y1 = Math.min(at[1] + n[1], vhi[1]);
    const z0 = Math.max(at[2], vlo[2]), z1 = Math.min(at[2] + n[2], vhi[2]);
    const pos = [0, 0, 0];
    for (let z = z0; z < z1; z++) for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
      let low: number, high: number;
      if (compressed) {
        pos[0] = x - at[0]; pos[1] = y - at[1]; pos[2] = z - at[2];
        ngeReadCompressedUint64(value, data, base, n, block, pos);
        low = value.low; high = value.high;
      } else {
        const k = ((x - at[0]) + n[0] * ((y - at[1]) + n[1] * (z - at[2]))) * 2;
        low = data[k]; high = data[k + 1];
      }
      const side = sideOf.get(high >>> 0)?.get(low >>> 0);
      if (!side) continue;
      labels[(x - vlo[0]) + dims[0] * ((y - vlo[1]) + dims[1] * (z - vlo[2]))] = side;
      if (side === 1) marked.red++; else marked.blue++;
    }
  };
  const worker = async () => { for (;;) { const g = todo.pop(); if (!g) return; await readOne(g); } };
  await Promise.all([worker(), worker(), worker(), worker(), worker(), worker()]);
  const res = Array.from(scale.resolution as Float64Array).slice(0, 3);
  return {
    ok: files.read > 0, why: files.read > 0 ? '' : 'None of the volume\'s files round the points could be read.',
    stored, originNm: [0, 1, 2].map(i => vlo[i] * res[i]), voxelNm: res, dims, labels, files, marked,
  };
}

/** The block of the volume each id lies in ([x0, y0, z0, x1, y1, z1] in
 *  nanometres), for supervoxels and for pieces alike. Read from the id. */
export async function ngeGrapheneBoxesOf(layer: SegmentationUserLayer, ids: string[]): Promise<Map<string, number[]>> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  return (connection.graph.graphServer as any).ngeChunkBoxes(ids);
}

/** The piece each of a FEW supervoxels belongs to, one request each, a few
 *  at a time (the same request Highlight mode makes for a click). One the
 *  server does not answer for is left out. */
export async function ngeGraphenePiecesOf(layer: SegmentationUserLayer, supervoxelIds: string[]): Promise<Map<string, string>> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) throw new Error('This layer has no proofreading graph.');
  const server = connection.graph.graphServer as any;
  const out = new Map<string, string>();
  const todo = [...new Set(supervoxelIds)];
  const next = async () => {
    for (;;) {
      const sv = todo.pop();
      if (!sv) return;
      try { out.set(sv, await server.ngePieceOf(sv)); } catch { /* left out */ }
    }
  };
  await Promise.all([next(), next(), next(), next(), next(), next()]);
  return out;
}

/** How thick a cell is in each of its pieces, nm (empty where the dataset
 *  keeps no such store). Lantern's Clicked branch finds the soma with it. */
export async function ngeGrapheneThickness(layer: SegmentationUserLayer, ids: string[]): Promise<Map<string, number>> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) return new Map();
  return (connection.graph.graphServer as any).ngePieceThickness(ids);
}

/** The piece of its cell that a pick landed in, or undefined. */
export async function ngeGraphenePieceOf(layer: SegmentationUserLayer, pick: SegmentSelection): Promise<string|undefined> {
  const connection = layer.graphConnection.value;
  if (!(connection instanceof GraphConnection)) return undefined;
  try { return await (connection.graph.graphServer as any).ngePieceOf(pick.segmentId.toString()); } catch { return undefined; }
}
